/* global __firebase_config, __app_id, QRCode */
import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import {
    getAuth,
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    signInAnonymously,
    sendPasswordResetEmail,
    EmailAuthProvider,
    reauthenticateWithCredential,
    updatePassword
} from 'firebase/auth';
import {
    getFirestore,
    doc,
    getDoc,
    setDoc,
    getDocs,
    collection,
    query,
    onSnapshot,
    runTransaction,
    addDoc,
    serverTimestamp,
    deleteDoc,
    updateDoc,
    where
} from 'firebase/firestore';

// --- Firebase Configuration ---
const firebaseConfig = typeof __firebase_config !== 'undefined'
    ? JSON.parse(__firebase_config)
    : {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID
      };

const appId = typeof __app_id !== 'undefined'
    ? __app_id
    : (process.env.REACT_APP_CUSTOM_APP_ID || 'default-penaltypoker-app');

// --- Initialize Firebase ---
let app;
let auth;
let db;

const isConfigValid = firebaseConfig && firebaseConfig.apiKey && !firebaseConfig.apiKey.includes("fallback");
if (isConfigValid) {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
}

// --- Audio Cue Functions ---
const playSound = async (type) => {
    try {
        let soundFile = '';
        if (type === 'whistle') {
            soundFile = '/whistle.mp3';
        } else if (type === 'horn') {
            soundFile = '/horn.mp3';
        }

        if (soundFile) {
            const audio = new Audio(soundFile);
            await audio.play();
        }
    } catch (error) {
        if (error.name !== 'NotAllowedError') {
            console.error("Audio playback error:", error);
        }
    }
};


// --- Helper & Hand Evaluation Functions ---
const SUITS = ['♥', '♦', '♣', '♠'];
const VALUES = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
const CARD_VALUES = { '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14 };

const createDeck = () => {
    const valueMap = { 'A': 'ace', 'K': 'king', 'Q': 'queen', 'J': 'jack', '10': '10', '9': '9', '8': '8', '7': '7', '6': '6', '5': '5', '4': '4', '3': '3', '2': '2' };
    const suitMap = { '♥': 'hearts', '♦': 'diamonds', '♣': 'clubs', '♠': 'spades' };
    const shoe = [];
    const numberOfDecks = 15;

    for (let i = 0; i < numberOfDecks; i++) {
        const singleDeck = [];
        for (const suit of SUITS) {
            for (const value of VALUES) {
                const valueStr = valueMap[value];
                const suitStr = suitMap[suit];
                singleDeck.push({
                    suit,
                    value,
                    id: `${value}-${suit}-${i}`,
                    image: `https://raw.githubusercontent.com/hayeah/playing-cards-assets/master/png/${valueStr}_of_${suitStr}.png`
                });
            }
        }
        for (let j = singleDeck.length - 1; j > 0; j--) {
            const k = Math.floor(Math.random() * (j + 1));
            [singleDeck[j], singleDeck[k]] = [singleDeck[k], singleDeck[j]];
        }
        shoe.push(...singleDeck);
    }
    return shoe;
};

function getCombinations(array, size) {
    const result = [];
    function combination(temp, start) {
        if (temp.length === size) {
            result.push([...temp]);
            return;
        }
        for (let i = start; i < array.length; i++) {
            temp.push(array[i]);
            combination(temp, i + 1);
            temp.pop();
        }
    }
    combination([], 0);
    return result;
}

const evaluate5CardHand = (hand) => {
    if (!hand || hand.length !== 5) return { rank: 0, name: 'Invalid Hand', values: [] };

    const sortedValues = hand.map(c => CARD_VALUES[c.value]).sort((a, b) => b - a);
    const suits = hand.map(c => c.suit);
    const isFlush = new Set(suits).size === 1;

    const isAceLowStraight = JSON.stringify(sortedValues) === JSON.stringify([14, 5, 4, 3, 2]);
    let isStraight = sortedValues.every((v, i) => i === 0 || sortedValues[i-1] - 1 === v);
    if(isAceLowStraight) isStraight = true;

    if (isStraight && isFlush) {
        if (isAceLowStraight) return { rank: 9, name: 'Steel Wheel', values: [5, 4, 3, 2, 1] };
        if (sortedValues[0] === 14) return { rank: 10, name: 'Royal Flush', values: sortedValues };
        return { rank: 9, name: 'Straight Flush', values: sortedValues };
    }

    const valueCounts = sortedValues.reduce((acc, v) => {
        acc[v] = (acc[v] || 0) + 1;
        return acc;
    }, {});

    const counts = Object.values(valueCounts).sort((a, b) => b - a);
    const primaryValues = Object.keys(valueCounts).sort((a, b) => valueCounts[b] - valueCounts[a] || b - a).map(Number);

    if (counts[0] === 4) return { rank: 8, name: 'Four of a Kind', values: primaryValues };
    if (counts[0] === 3 && counts[1] === 2) return { rank: 7, name: 'Full House', values: primaryValues };
    if (isFlush) return { rank: 6, name: 'Flush', values: sortedValues };
    if (isStraight) {
        if(isAceLowStraight) return { rank: 5, name: 'Straight (Wheel)', values: [5, 4, 3, 2, 1] };
        return { rank: 5, name: 'Straight', values: sortedValues };
    }
    if (counts[0] === 3) return { rank: 4, name: 'Three of a Kind', values: primaryValues };
    if (counts[0] === 2 && counts[1] === 2) return { rank: 3, name: 'Two Pair', values: primaryValues };
    if (counts[0] === 2) return { rank: 2, name: 'One Pair', values: primaryValues };

    return { rank: 1, name: 'High Card', values: sortedValues };
};

const evaluatePartialHand = (hand) => {
    if (!hand || hand.length === 0) return { rank: 0, name: 'No Cards', values: [] };

    const sortedValues = hand.map(c => CARD_VALUES[c.value]).sort((a, b) => b - a);
    const valueCounts = sortedValues.reduce((acc, v) => {
        acc[v] = (acc[v] || 0) + 1;
        return acc;
    }, {});

    const counts = Object.values(valueCounts).sort((a, b) => b - a);
    const primaryValues = Object.keys(valueCounts).sort((a, b) => valueCounts[b] - valueCounts[a] || b - a).map(Number);

    if (counts[0] === 4) return { rank: 8, name: 'Four of a Kind', values: primaryValues, best5CardHand: hand };
    if (counts[0] === 3) return { rank: 4, name: 'Three of a Kind', values: primaryValues, best5CardHand: hand };
    if (counts[0] === 2 && counts[1] === 2) return { rank: 3, name: 'Two Pair', values: primaryValues, best5CardHand: hand };
    if (counts[0] === 2) return { rank: 2, name: 'One Pair', values: primaryValues, best5CardHand: hand };

    return { rank: 1, name: 'High Card', values: sortedValues, best5CardHand: hand };
};

const evaluateBestHand = (hand) => {
    if (!hand || hand.length === 0) {
        return { rank: 0, name: 'No Cards', values: [], best5CardHand: [] };
    }

    if (hand.length < 5) {
        return evaluatePartialHand(hand);
    }

    const combinations = getCombinations(hand, 5);
    let bestHandResult = { rank: -1, name: 'No Hand', values: [], best5CardHand: [] };

    for (const combo of combinations) {
        const currentHandResult = evaluate5CardHand(combo);
        if (currentHandResult.rank > bestHandResult.rank) {
            bestHandResult = { ...currentHandResult, best5CardHand: combo };
        } else if (currentHandResult.rank === bestHandResult.rank) {
            for (let i = 0; i < currentHandResult.values.length; i++) {
                if (currentHandResult.values[i] > bestHandResult.values[i]) {
                    bestHandResult = { ...currentHandResult, best5CardHand: combo };
                    break;
                }
                if (currentHandResult.values[i] < bestHandResult.values[i]) {
                    break;
                }
            }
        }
    }
    return bestHandResult;
};

const sortPlayerHand = (cards) => {
    if (!cards || cards.length === 0) return [];

    const handDetails = evaluateBestHand(cards);
    
    if (!handDetails || !handDetails.best5CardHand || handDetails.best5CardHand.length === 0) {
        return [...cards].sort((a, b) => CARD_VALUES[b.value] - CARD_VALUES[a.value]);
    }

    const best5 = handDetails.best5CardHand;
    const handName = handDetails.name;
    let sortedBest5;

    if (handName.includes('Straight') || handName.includes('Flush')) {
        const isWheel = handName.includes('Wheel');
        sortedBest5 = [...best5].sort((a, b) => {
            let valA = CARD_VALUES[a.value];
            let valB = CARD_VALUES[b.value];
            if (isWheel) {
                if (valA === 14) valA = 1;
                if (valB === 14) valB = 1;
            }
            return valA - valB;
        });
    } else {
        const valueCounts = best5.reduce((acc, card) => {
            acc[card.value] = (acc[card.value] || 0) + 1;
            return acc;
        }, {});
        sortedBest5 = [...best5].sort((a, b) => {
            const countA = valueCounts[a.value];
            const countB = valueCounts[b.value];
            const valA = CARD_VALUES[a.value];
            const valB = CARD_VALUES[b.value];

            if (countB !== countA) return countB - countA;
            return valB - valA;
        });
    }
    
    const best5Ids = new Set(sortedBest5.map(c => c.id));
    const remainingCards = cards
        .filter(c => !best5Ids.has(c.id))
        .sort((a,b) => CARD_VALUES[b.value] - CARD_VALUES[a.value]);

    return [...sortedBest5, ...remainingCards];
};


// --- Gemini API Helper ---
const callGeminiAPI = async (prompt) => {
    const apiKey = "";
    const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-flash-1.5:generateContent?key=${apiKey}`;

    const payload = {
        contents: [{ role: "user", parts: [{ text: prompt }] }]
    };

    try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error(`API call failed with status: ${response.status}`);
        }

        const result = await response.json();

        if (result.candidates && result.candidates.length > 0 &&
            result.candidates[0].content && result.candidates[0].content.parts &&
            result.candidates[0].content.parts.length > 0) {
            return result.candidates[0].content.parts[0].text;
        } else {
            throw new Error("Unexpected response structure from API.");
        }
    } catch (error) {
        console.error("Gemini API call error:", error);
        return "Error generating content. Please try again.";
    }
};


// --- Components ---

const Header = () => (
    <div className="w-full max-w-4xl mx-auto mb-4">
        <img src="/logo.png" alt="Penalty Poker Logo" className="h-24 mx-auto" />
    </div>
);

const ConfigurationError = () => (
    <div className="w-full max-w-2xl bg-red-900 border-4 border-red-500 text-white p-8 rounded-lg shadow-2xl text-center">
        <h1 className="text-4xl font-bold mb-4">Configuration Error</h1>
        <p className="text-lg">The Firebase API keys are missing or invalid.</p>
        <p className="mt-4">If you are running this application locally or deploying it, you must create a <code>.env</code> file in the root of your project and add your Firebase project configuration keys.</p>
        <p className="mt-2 font-mono bg-gray-800 p-2 rounded">Example: REACT_APP_FIREBASE_API_KEY="your-key-here"</p>
    </div>
);


const HomePage = ({ setPage, user, installPrompt }) => (
    <div className="text-center bg-gray-900/70 backdrop-blur-sm p-8 rounded-lg shadow-2xl">
        {user && (user.role === 'host' || user.role === 'admin') && (
            <div className="mb-8">
                <button onClick={() => setPage('hostDashboard')} className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-4 px-8 rounded-lg shadow-lg transition transform hover:scale-105 text-xl">
                    Go to Your Dashboard
                </button>
            </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <button onClick={() => setPage('activeGames')} className="md:col-span-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-8 px-6 rounded-lg shadow-lg transition transform hover:scale-105 text-xl">
                Click here to view Active Games
            </button>
            <button onClick={() => setPage('hostLogin')} className="bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition transform hover:scale-105">
                Click Here to Host a Game
            </button>
             {installPrompt && (
                <button onClick={installPrompt} className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition transform hover:scale-105">
                    Install App
                </button>
            )}
            <button onClick={() => setPage('howToOnline')} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition transform hover:scale-105">
                How to Play Online
            </button>
            <button onClick={() => setPage('howToManual')} className="md:col-span-2 bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition transform hover:scale-105">
                How to Play Manually
            </button>
        </div>
    </div>
);

const HowToPlayOnline = ({ setPage }) => (
    <div className="max-w-3xl mx-auto bg-gray-800/80 backdrop-blur-sm p-8 rounded-lg shadow-2xl">
        <h2 className="text-3xl font-bold mb-4 text-yellow-400">How to Play Penalty Poker Online</h2>
        <div className="text-gray-300 space-y-4">
            <p><strong>For Players:</strong> First, create a unique username. Then, you'll see a list of active games. Choose one to join and wait for the host to approve you. Once in, you'll see your card area. The host deals cards, and your hand will fill up automatically.</p>
            <p><strong>For Hosts:</strong> Register for a host account. From your dashboard, you can create new games. When players request to join, you can approve them from your game management page. Once you're ready, lock the game to start dealing cards to all approved players. After dealing, you can end the hand to determine a winner. A game consists of 4 hands.</p>
        </div>
        <button onClick={() => setPage('home')} className="mt-8 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">Back to Home</button>
    </div>
);

const HowToPlayManually = ({ setPage }) => (
     <div className="max-w-3xl mx-auto bg-gray-800/80 backdrop-blur-sm p-8 rounded-lg shadow-2xl">
        <h2 className="text-3xl font-bold mb-4 text-yellow-400">How to Play Penalty Poker with Physical Cards</h2>
        <div className="text-gray-300 space-y-4">
            <p>Penalty Poker can be played with a standard 52-card deck. The goal is to have the best poker hand at the end of each round.</p>
            <p><strong>Setup:</strong> One person acts as the dealer (the "host"). Players sit around a table. Each player has a space in front of them for their cards.</p>
            <p><strong>Dealing:</strong> The dealer shuffles the deck and deals cards one by one to each player according to the game variant being played (e.g., one card face up, one face down).</p>
            <p><strong>Quarters:</strong> A game is typically divided into four "quarters." Each quarter consists of a predetermined number of hands. Points are awarded based on hand rankings at the end of each hand, and a quarter winner is determined by the total points.</p>
        </div>
        <button onClick={() => setPage('home')} className="mt-8 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">Back to Home</button>
    </div>
);

const PlayerJoinModal = ({ game, onClose, user, setPage, setGameId, setUser }) => {
    const [username, setUsername] = useState(user?.username || '');
    const [gameCode, setGameCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [paymentStep, setPaymentStep] = useState(false);
    const [hostPaymentInfo, setHostPaymentInfo] = useState(null);
    const [selectedQuarters, setSelectedQuarters] = useState([]);

    const isBuyInGame = game.buyInEnabled && game.buyInAmount > 0;

    useEffect(() => {
        if (game) {
            const availableQuarters = [1, 2, 3, 4].filter(q => q >= (game.currentHand || 1));
            setSelectedQuarters(availableQuarters);
        }
    }, [game]);

    useEffect(() => {
        if (isBuyInGame && game.hostId) {
            const paymentInfoRef = doc(db, `artifacts/${appId}/users/${game.hostId}/paymentInfo/methods`);
            getDoc(paymentInfoRef).then(docSnap => {
                if (docSnap.exists()) {
                    setHostPaymentInfo(docSnap.data());
                } else {
                    console.log("Host payment info not found.");
                }
            });
        }
    }, [isBuyInGame, game.hostId]);

    const handleQuarterChange = (q) => {
        setSelectedQuarters(prev => 
            prev.includes(q) ? prev.filter(item => item !== q) : [...prev, q]
        );
    };

    const handleJoin = async (paymentStatus = null) => {
        if (!username.trim()) {
            setError('Username cannot be empty.');
            return;
        }
        if (game.gameCode && gameCode !== game.gameCode) {
            setError('Invalid game code.');
            return;
        }
        if (!user || !user.uid) {
            setError('Could not identify user. Please refresh and try again.');
            return;
        }

        if (isBuyInGame && !paymentStep) {
            setPaymentStep(true);
            return;
        }
        
        setLoading(true);
        setError('');

        try {
            // Username uniqueness check... (omitted for brevity, but it's here)

            const gameRef = doc(db, `artifacts/${appId}/public/data/games`, game.id);
            await runTransaction(db, async (transaction) => {
                const gameDoc = await transaction.get(gameRef);
                if (!gameDoc.exists()) throw new Error("Game does not exist anymore.");
                const gameData = gameDoc.data();

                if ((gameData.currentHand || 1) > 3) {
                    throw new Error("This game is in its final quarter and is no longer accepting new players.");
                }

                const isPending = gameData.pendingPlayers?.some(p => p.uid === user.uid);
                const isApproved = gameData.approvedPlayers && gameData.approvedPlayers[user.uid];

                if (isPending || isApproved) {
                    throw new Error("You have already requested to join this game.");
                }

                const pending = gameData.pendingPlayers || [];
                const playerInfo = { 
                    username, 
                    uid: user.uid,
                    email: user.email || null,
                    activeQuarters: selectedQuarters, // Save selected quarters
                    paymentStatus: paymentStatus
                };

                pending.push(playerInfo);
                transaction.update(gameRef, { pendingPlayers: pending });
            });

            if(!user.username) {
                setUser(prev => ({...prev, username}));
            }
            setGameId(game.id);
            setPage('gamePage');
            onClose();

        } catch (e) {
            console.error("Error joining game:", e);
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };
    
    const renderPaymentStep = () => {
        const totalAmount = selectedQuarters.length * game.buyInAmount;
        const availableQuarters = [1, 2, 3, 4].filter(q => q >= (game.currentHand || 1));

        return (
            <div>
                <h3 className="text-xl font-bold text-white mb-2">Pay Buy-in: ${totalAmount}</h3>
                <p className="text-gray-300 mb-4">Select the quarters you are paying for.</p>
                <div className="flex justify-center gap-4 mb-4 text-white">
                    {availableQuarters.map(q => (
                        <label key={q} className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={selectedQuarters.includes(q)}
                                onChange={() => handleQuarterChange(q)}
                                className="h-5 w-5"
                            />
                            Q{q}
                        </label>
                    ))}
                </div>

                <p className="text-gray-300 mb-4">1. Click a link below to pay the host, <strong>{game.hostAlias}</strong>.</p>
                <div className="space-y-3 mb-6">
                    {hostPaymentInfo?.venmo && <a href={`https://venmo.com/${hostPaymentInfo.venmo}`} target="_blank" rel="noopener noreferrer" className="block w-full bg-blue-500 text-white font-bold py-3 px-4 rounded-lg hover:bg-blue-600">Pay with Venmo</a>}
                    {hostPaymentInfo?.cashapp && <a href={`https://cash.app/$${hostPaymentInfo.cashapp}`} target="_blank" rel="noopener noreferrer" className="block w-full bg-green-500 text-white font-bold py-3 px-4 rounded-lg hover:bg-green-600">Pay with Cash App</a>}
                    {hostPaymentInfo?.paypal && <a href={`https://paypal.me/${hostPaymentInfo.paypal}`} target="_blank" rel="noopener noreferrer" className="block w-full bg-blue-700 text-white font-bold py-3 px-4 rounded-lg hover:bg-blue-800">Pay with PayPal</a>}
                </div>
                <p className="text-gray-300 mb-4">2. Click the button that matches the service you used to confirm.</p>
                 <div className="flex flex-col gap-3">
                    {hostPaymentInfo?.venmo && <button onClick={() => handleJoin('paid_venmo')} disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-6 rounded-lg disabled:bg-blue-300">{loading ? 'Submitting...' : "I Paid with Venmo"}</button>}
                    {hostPaymentInfo?.cashapp && <button onClick={() => handleJoin('paid_cashapp')} disabled={loading} className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-lg disabled:bg-green-300">{loading ? 'Submitting...' : "I Paid with Cash App"}</button>}
                    {hostPaymentInfo?.paypal && <button onClick={() => handleJoin('paid_paypal')} disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800 text-white font-bold py-2 px-6 rounded-lg disabled:bg-blue-400">{loading ? 'Submitting...' : "I Paid with PayPal"}</button>}
                     <button onClick={() => handleJoin('in_person')} disabled={loading} className="w-full bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg disabled:bg-gray-400 mt-2">
                        {loading ? 'Submitting...' : "I Will Pay in Person/Cash"}
                    </button>
                </div>
                <button onClick={() => setPaymentStep(false)} className="text-gray-400 mt-4">Back</button>
            </div>
        )
    };

    const renderInitialStep = () => (
        <div>
            <h3 className="text-xl font-bold text-white mb-2">Join Game: {game.gameName}</h3>
            {error && <p className="bg-red-500 text-white p-2 rounded mb-4 text-sm">{error}</p>}
            <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                readOnly={!!user?.username}
                className="w-full bg-gray-700 text-white p-3 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-600"
            />
            {game.gameCode && (
                <input
                    type="number"
                    value={gameCode}
                    onChange={(e) => setGameCode(e.target.value)}
                    placeholder="Enter Game Code"
                    className="w-full bg-gray-700 text-white p-3 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            )}
            <div className="flex justify-center gap-4">
                <button onClick={() => handleJoin()} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg disabled:bg-blue-400">
                    {loading ? 'Joining...' : (isBuyInGame ? 'Next' : 'Request to Join')}
                </button>
                <button onClick={onClose} className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-lg">Cancel</button>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-6 rounded-lg shadow-xl text-center w-full max-w-sm">
                {paymentStep ? renderPaymentStep() : renderInitialStep()}
            </div>
        </div>
    );
};

const HostPortal = ({ setPage }) => {
    const [isRegistering, setIsRegistering] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [verifyPassword, setVerifyPassword] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const handleAuth = async (e) => {
        e.preventDefault();
        setError('');

        if (isRegistering) {
            if (password.length < 8) { setError('Password must be at least 8 characters long.'); return; }
            if (password !== verifyPassword) { setError('Passwords do not match.'); return; }
        }

        try {
            if (isRegistering) {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                await setDoc(doc(db, `artifacts/${appId}/users`, userCredential.user.uid), { role: 'host', email: email });
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
            setPage('home');
        } catch (err) {
            setError(err.message);
        }
    };
    
    const handlePasswordReset = async () => {
        if (!email) {
            setError("Please enter your email address to reset your password.");
            return;
        }
        try {
            await sendPasswordResetEmail(auth, email);
            setMessage("Password reset email sent! Please check your inbox.");
            setError("");
        } catch (err) {
            setError("Could not send reset email. Please ensure the email is correct.");
        }
    };

    return (
        <div className="w-full max-w-sm bg-gray-800/80 backdrop-blur-sm p-8 rounded-lg shadow-2xl">
            <h2 className="text-2xl font-bold text-center text-white mb-6">{isRegistering ? 'Host Registration' : 'Host Login'}</h2>
            {error && <p className="bg-red-500 text-white p-2 rounded mb-4 text-sm">{error}</p>}
            {message && <p className="bg-green-500 text-white p-2 rounded mb-4 text-sm">{message}</p>}
            <form onSubmit={handleAuth}>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (used as username)" className="w-full bg-gray-700 text-white p-3 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-green-500"/>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="w-full bg-gray-700 text-white p-3 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-green-500"/>
                {isRegistering && (
                    <input type="password" value={verifyPassword} onChange={(e) => setVerifyPassword(e.target.value)} placeholder="Verify Password" className="w-full bg-gray-700 text-white p-3 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-green-500"/>
                )}
                <button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg">{isRegistering ? 'Register' : 'Login'}</button>
            </form>
            {!isRegistering && <button onClick={handlePasswordReset} className="w-full mt-2 text-sm text-gray-400 hover:text-white">Forgot Password?</button>}
            <button onClick={() => setIsRegistering(!isRegistering)} className="w-full mt-4 text-gray-400 hover:text-white">{isRegistering ? 'Already have an account? Login' : "Don't have an account? Register"}</button>
             <button onClick={() => setPage('home')} className="w-full mt-2 text-gray-400 hover:text-white">Back to Home</button>
        </div>
    );
};

const PlayerAuthPortal = ({ setPage }) => {
    const [isRegistering, setIsRegistering] = useState(false);
    const [emailOrUsername, setEmailOrUsername] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [verifyPassword, setVerifyPassword] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const handleAuth = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (isRegistering) {
            if (password.length < 8) { setError('Password must be at least 8 characters long.'); return; }
            if (password !== verifyPassword) { setError('Passwords do not match.'); return; }
            if (!username.trim()) { setError('Username cannot be empty.'); return; }
        }

        try {
            if (isRegistering) {
                const userCredential = await createUserWithEmailAndPassword(auth, emailOrUsername, password);
                await setDoc(doc(db, `artifacts/${appId}/users`, userCredential.user.uid), { 
                    role: 'player', 
                    username: username,
                    email: emailOrUsername
                });
                setMessage('Registration successful! You can now log in.');
                setIsRegistering(false);
            } else {
                let finalEmail = emailOrUsername;
                // Check if input is likely a username (doesn't contain '@')
                if (!emailOrUsername.includes('@')) {
                    const usersRef = collection(db, `artifacts/${appId}/users`);
                    const q = query(usersRef, where("username", "==", emailOrUsername));
                    const querySnapshot = await getDocs(q);
                    if (!querySnapshot.empty) {
                        // Found user by username, get their email
                        finalEmail = querySnapshot.docs[0].data().email;
                    } else {
                        throw new Error("User not found.");
                    }
                }
                await signInWithEmailAndPassword(auth, finalEmail, password);
                setPage('home');
            }
        } catch (err) {
            setError("Login failed. Please check your credentials.");
            console.error(err);
        }
    };
    
    const handlePasswordReset = async () => {
        if (!emailOrUsername) {
            setError("Please enter your email address to reset your password.");
            return;
        }
        try {
            await sendPasswordResetEmail(auth, emailOrUsername);
            setMessage("Password reset email sent! Please check your inbox.");
            setError("");
        } catch (err) {
            setError("Could not send reset email. Please ensure the email is correct.");
        }
    };

    return (
        <div className="w-full max-w-sm bg-gray-800/80 backdrop-blur-sm p-8 rounded-lg shadow-2xl">
            <h2 className="text-2xl font-bold text-center text-white mb-6">{isRegistering ? 'Player Registration' : 'Player Login'}</h2>
            {error && <p className="bg-red-500 text-white p-2 rounded mb-4 text-sm">{error}</p>}
            {message && <p className="bg-green-500 text-white p-2 rounded mb-4 text-sm">{message}</p>}
            <form onSubmit={handleAuth}>
                <input type="text" value={emailOrUsername} onChange={(e) => setEmailOrUsername(e.target.value)} placeholder={isRegistering ? "Email Address" : "Email or Username"} className="w-full bg-gray-700 text-white p-3 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                {isRegistering && (
                    <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username / Alias" className="w-full bg-gray-700 text-white p-3 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                )}
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="w-full bg-gray-700 text-white p-3 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                {isRegistering && (
                    <input type="password" value={verifyPassword} onChange={(e) => setVerifyPassword(e.target.value)} placeholder="Verify Password" className="w-full bg-gray-700 text-white p-3 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                )}
                <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg">{isRegistering ? 'Register' : 'Login'}</button>
            </form>
            <div className="text-center">
                 {!isRegistering && <button onClick={handlePasswordReset} className="w-full mt-2 text-sm text-gray-400 hover:text-white">Forgot Password?</button>}
                <button onClick={() => setIsRegistering(!isRegistering)} className="w-full mt-4 text-gray-400 hover:text-white">{isRegistering ? 'Already have an account? Login' : "Don't have an account? Register"}</button>
                <button onClick={() => setPage('activeGames')} className="w-full mt-2 text-gray-400 hover:text-white">Continue as Guest</button>
            </div>
        </div>
    );
};

const ActiveGamesList = ({ setPage, user, setGameId, setUser, filterHostId }) => {
    const [allGames, setAllGames] = useState([]);
    const [error, setError] = useState('');
    const [gameToJoin, setGameToJoin] = useState(null);
    const [selectedHostId, setSelectedHostId] = useState(null);

    useEffect(() => {
        if (filterHostId) {
            setSelectedHostId(filterHostId);
        }
    }, [filterHostId]);

    useEffect(() => {
        const q = query(collection(db, `artifacts/${appId}/public/data/games`));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const gamesList = [];
            querySnapshot.forEach((doc) => {
                gamesList.push({ id: doc.id, ...doc.data() });
            });
            setAllGames(gamesList);
        }, (err) => {
            console.error(err);
            setError("Could not fetch games list. Check Firestore rules.");
        });
        return () => unsubscribe();
    }, []);

    const myApprovedGames = allGames.filter(game => game.approvedPlayers && game.approvedPlayers[user?.uid]);
    const myPendingGames = allGames.filter(game => game.status !== 'finished' && game.pendingPlayers?.some(p => p.uid === user?.uid));
    
    const myGameIds = new Set([
        ...myApprovedGames.map(g => g.id),
        ...myPendingGames.map(g => g.id)
    ]);

    const renderHostList = () => {
        const activeHosts = allGames.reduce((acc, game) => {
            if (game.status !== 'finished') {
                if (!acc.has(game.hostId)) {
                    acc.set(game.hostId, {
                        hostId: game.hostId,
                        hostAlias: game.hostAlias || 'Unnamed Host',
                        gameCount: 0
                    });
                }
                acc.get(game.hostId).gameCount++;
            }
            return acc;
        }, new Map());

        return (
            <div>
                <h3 className="text-2xl font-bold text-yellow-400 mb-3 border-b border-yellow-400/30 pb-2">Join a Game</h3>
                {Array.from(activeHosts.values()).length > 0 ? Array.from(activeHosts.values()).map(host => (
                    <button key={host.hostId} onClick={() => setSelectedHostId(host.hostId)} className="w-full text-left bg-gray-700 p-4 rounded-lg mb-2 hover:bg-gray-600">
                        <p className="font-bold text-lg text-white">{host.hostAlias}</p>
                        <p className="text-sm text-gray-300">{host.gameCount} active game(s)</p>
                    </button>
                )) : <p className="text-gray-400">No hosts have active games right now.</p>}
            </div>
        );
    };

    const renderGameListForHost = () => {
        const hostGames = allGames.filter(game => 
            game.hostId === selectedHostId && 
            game.status !== 'finished' &&
            !myGameIds.has(game.id)
        );
        const hostAlias = allGames.find(g => g.hostId === selectedHostId)?.hostAlias || 'Host';

        return (
            <div>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-2xl font-bold text-yellow-400">{hostAlias}'s Games</h3>
                    <button onClick={() => setSelectedHostId(null)} className="text-sm text-gray-400 hover:text-white">&larr; Back to All Hosts</button>
                </div>
                {hostGames.length > 0 ? hostGames.map(game => (
                    <div key={game.id} className="bg-gray-700 p-4 rounded-lg flex justify-between items-center mb-2">
                        <div>
                            <p className="font-bold text-lg text-white">{game.gameName}</p>
                            {game.buyInEnabled && <p className="text-sm text-green-400 font-bold">${game.buyInAmount} Buy-in</p>}
                        </div>
                        <button onClick={() => setGameToJoin(game)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">Request to Join</button>
                    </div>
                )) : <p className="text-gray-400">No available games from this host. You may have already joined them.</p>}
            </div>
        );
    };

    return (
         <>
            <div className="w-full max-w-3xl bg-gray-800/80 backdrop-blur-sm p-8 rounded-lg shadow-2xl">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-3xl font-bold text-white">Game Lobby</h2>
                        {user?.role === 'player' && <p className="text-yellow-400">Welcome, {user.username}!</p>}
                    </div>
                    <div className="flex items-center">
                        <button onClick={() => setPage('home')} className="text-sm text-gray-400 hover:text-white mr-4">&larr; Home</button>
                        {(!user || user.role === 'guest') && (
                            <button onClick={() => setPage('playerLogin')} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">
                                Player Login/Sign Up
                            </button>
                        )}
                        {(user && user.role !== 'guest') && (
                            <>
                            <button onClick={() => setPage('account')} className="text-sm text-gray-400 hover:text-white mr-4">Account</button>
                            <button onClick={async () => await signOut(auth)} className="text-sm text-gray-400 hover:text-white">
                            Logout
                            </button>
                            </>
                        )}
                    </div>
                </div>
                 {error && <p className="bg-red-500 text-white p-2 rounded mb-4">{error}</p>}

                {(myApprovedGames.length > 0 || myPendingGames.length > 0) && (
                    <div className="mb-8">
                        <h3 className="text-2xl font-bold text-yellow-400 mb-3 border-b border-yellow-400/30 pb-2">My Games</h3>
                        <div className="space-y-2">
                            {myApprovedGames.map(game => (
                                <div key={game.id} className={`${game.status === 'finished' ? 'bg-gray-800/50' : 'bg-green-800/50'} p-4 rounded-lg flex justify-between items-center`}>
                                    <div>
                                        <p className="font-bold text-lg text-white">{game.gameName}</p>
                                        <p className="text-sm text-gray-300">Host: {game.hostAlias} {game.status === 'finished' && <span className="text-red-400 font-bold">(Finished)</span>}</p>
                                    </div>
                                    <button onClick={() => {
                                        setGameId(game.id);
                                        setPage('gamePage');
                                    }} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg">View {game.status === 'finished' ? 'Results' : 'Hand'}</button>
                                </div>
                            ))}
                            {myPendingGames.map(game => {
                                const pendingPlayerInfo = game.pendingPlayers.find(p => p.uid === user?.uid);
                                const pendingUsername = pendingPlayerInfo ? pendingPlayerInfo.username : '';
                                return (
                                    <div key={game.id} className="bg-gray-700 p-4 rounded-lg flex justify-between items-center">
                                        <div>
                                            <p className="font-bold text-lg text-white">{game.gameName}</p>
                                            <p className="text-sm text-gray-300">Host: {game.hostAlias}</p>
                                        </div>
                                        <span className="text-yellow-400 font-semibold">
                                            Pending Approval as "{pendingUsername}"
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {selectedHostId ? renderGameListForHost() : renderHostList()}
            </div>
             {gameToJoin && <PlayerJoinModal
                game={gameToJoin}
                onClose={() => setGameToJoin(null)}
                user={user}
                setPage={setPage}
                setGameId={setGameId}
                setUser={setUser}
            />}
        </>
    )
};

const HostDashboard = ({ setPage, user, setGameId }) => {
    const [games, setGames] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [newGameName, setNewGameName] = useState('');
    const [newGameCode, setNewGameCode] = useState('');
    const [isSuggestingName, setIsSuggestingName] = useState(false);
    const [error, setError] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [gameToDelete, setGameToDelete] = useState(null);
    
    const [potType, setPotType] = useState('dynamic'); // 'dynamic' or 'static'
    const [staticPotDescription, setStaticPotDescription] = useState('');
    const [buyInEnabled, setBuyInEnabled] = useState(false);
    const [buyInAmount, setBuyInAmount] = useState('5');

    const [showBuyInWarning, setShowBuyInWarning] = useState(false);
    const [showQrCodeModal, setShowQrCodeModal] = useState(false);
    const [showShareLinkModal, setShowShareLinkModal] = useState(false);
    const [showEmailInviteModal, setShowEmailInviteModal] = useState(false);
    const [gameToShare, setGameToShare] = useState(null);
    const [contacts, setContacts] = useState([]);
    
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    useEffect(() => {
        if (!user || !user.uid) return;

        const gamesQuery = query(collection(db, `artifacts/${appId}/public/data/games`), where("hostId", "==", user.uid));
        const unsubscribeGames = onSnapshot(gamesQuery, (querySnapshot) => {
            const gamesList = [];
            querySnapshot.forEach((doc) => {
                gamesList.push({ id: doc.id, ...doc.data() });
            });
            setGames(gamesList);
        });

        const contactsRef = collection(db, `artifacts/${appId}/users/${user.uid}/contacts`);
        const unsubscribeContacts = onSnapshot(contactsRef, (querySnapshot) => {
            const contactsList = [];
            querySnapshot.forEach((doc) => {
                contactsList.push({ id: doc.id, ...doc.data() });
            });
            setContacts(contactsList);
        });

        return () => {
            unsubscribeGames();
            unsubscribeContacts();
        };
    }, [user]);

    const handleSuggestName = async () => {
        setIsSuggestingName(true);
        const prompt = "Suggest a single, creative, and exciting name for a poker game night. The theme is American football. Keep it short and punchy.";
        const suggestion = await callGeminiAPI(prompt);
        setNewGameName(suggestion.replace(/["*]/g, ''));
        setIsSuggestingName(false);
    };

    const handleCreateGame = async () => {
        if (!user.alias) {
            setError("Please set your Host Alias in Account Management before creating a game.");
            return;
        }
        if (!newGameName.trim()) {
            setError("Game name cannot be empty.");
            return;
        }
        if (newGameCode && !/^\d{4,10}$/.test(newGameCode)) {
            setError("Game code must be a 4 to 10 digit number.");
            return;
        }
        if (potType === 'static' && !staticPotDescription.trim()) {
            setError("Please describe the static pot prize.");
            return;
        }

        setError('');
        const currentUser = auth.currentUser;
        if (!currentUser || currentUser.isAnonymous) {
            setError("You must be a logged-in host to create a game.");
            return;
        }
        try {
            const newGame = {
                gameName: newGameName,
                hostId: currentUser.uid,
                hostEmail: currentUser.email,
                hostAlias: user.alias,
                status: 'open',
                subStatus: null,
                pendingPlayers: [],
                approvedPlayers: {},
                deck: createDeck(),
                handWinners: [],
                playerHandHistories: {},
                currentHand: 1,
                potType: potType,
                buyInEnabled: potType === 'dynamic' ? buyInEnabled : false,
                buyInAmount: potType === 'dynamic' && buyInEnabled ? Number(buyInAmount) : 0,
                staticPotDescription: potType === 'static' ? staticPotDescription : '',
                gameCode: newGameCode || null,
                createdAt: serverTimestamp()
            };
            const docRef = await addDoc(collection(db, `artifacts/${appId}/public/data/games`), newGame);
            
            // Reset form
            setNewGameName('');
            setNewGameCode('');
            setShowCreate(false);
            setBuyInEnabled(false);
            setBuyInAmount('5');
            setPotType('dynamic');
            setStaticPotDescription('');
            
            setGameToShare({ id: docRef.id, ...newGame });
            setShowEmailInviteModal(true);

        } catch (e) {
            console.error(e);
            setError("Failed to create game. " + e.message);
        }
    };

    const handleSendInvites = async (manualEmails, includeContacts) => {
        if (!gameToShare) return { success: false, error: "No game selected." };

        let finalEmailList = [...manualEmails];
        if (includeContacts) {
            const contactEmails = contacts.map(c => c.email).filter(Boolean);
            finalEmailList = [...new Set([...finalEmailList, ...contactEmails])];
        }

        if (finalEmailList.length === 0) {
            return { success: false, error: "No emails to send." };
        }
        
        const functionUrl = 'https://sendemailinvites-ikzzqwyh3q-uc.a.run.app';
        const shareUrl = `https://penaltypoker.com?hostId=${user.uid}`;

        try {
            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    emails: finalEmailList,
                    hostAlias: user.alias,
                    shareUrl: shareUrl,
                }),
            });

            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`Failed to send invites: ${errorBody}`);
            }
            
            return { success: true };

        } catch (error) {
            console.error('Error sending invites:', error);
            return { success: false, error: error.message };
        }
    };

    const manageGame = (id) => { setGameId(id); setPage('gameManagement'); }

    const confirmDelete = (game) => {
        setGameToDelete(game);
        setShowDeleteModal(true);
    };

    const handleDeleteGame = async () => {
        if (!gameToDelete) return;
        const gameRef = doc(db, `artifacts/${appId}/public/data/games`, gameToDelete.id);
        try {
            await deleteDoc(gameRef);
        } catch (error) {
            console.error("Error deleting game:", error);
            setError("Failed to delete game.");
        } finally {
            setShowDeleteModal(false);
            setGameToDelete(null);
        }
    };

    const handleLogout = async () => { await signOut(auth); }

    return (
        <>
            <div className="w-full max-w-4xl bg-gray-800/80 backdrop-blur-sm p-8 rounded-lg shadow-2xl">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-3xl font-bold text-white">Host Dashboard</h2>
                    <div>
                        <button onClick={() => setPage('account')} className="text-sm text-gray-400 hover:text-white mr-4">Account</button>
                        {user.role === 'admin' && (
                            <button onClick={() => setPage('adminPanel')} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg">
                                Admin Panel
                            </button>
                        )}
                    </div>
                </div>

                {error && <p className="bg-red-500 text-white p-2 rounded mb-4">{error}</p>}

                <div className="flex flex-wrap gap-4 mb-6">
                    <button onClick={() => setShowCreate(true)} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg">+ Create New Game</button>
                    <button onClick={() => setShowPaymentModal(true)} className="bg-yellow-600 hover:bg-yellow-700 text-black font-bold py-2 px-6 rounded-lg">Setup Payment Methods</button>
                    <button onClick={() => setShowQrCodeModal(true)} className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-6 rounded-lg">Show My QR Code</button>
                    <button onClick={() => setShowShareLinkModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg">Share Link</button>
                    <button onClick={() => setPage('contacts')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-lg">My Contacts</button>
                </div>

                {showCreate && (
                    <div className="bg-gray-700 p-4 rounded-lg mb-6">
                        <h3 className="text-xl font-bold text-white mb-2">Create New Game</h3>
                        <div className="flex items-center gap-2 mb-4">
                            <input type="text" value={newGameName} onChange={(e) => setNewGameName(e.target.value)} placeholder="Enter unique game name" className="w-full bg-gray-600 text-white p-3 rounded focus:outline-none focus:ring-2 focus:ring-green-500"/>
                            <button onClick={handleSuggestName} disabled={isSuggestingName} className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded disabled:bg-purple-400">
                                {isSuggestingName ? '...' : '✨ Suggest'}
                            </button>
                        </div>
                         <input type="number" value={newGameCode} onChange={(e) => setNewGameCode(e.target.value)} placeholder="4-10 Digit Game Code (Optional)" className="w-full bg-gray-600 text-white p-3 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-green-500"/>
                        
                        <div className="mb-4">
                            <label className="text-white font-bold mb-2 block">Pot Type</label>
                            <div className="flex items-center gap-6">
                                <label className="flex items-center gap-2 text-white cursor-pointer">
                                    <input type="radio" name="potType" value="dynamic" checked={potType === 'dynamic'} onChange={() => setPotType('dynamic')} className="h-4 w-4"/>
                                    Dynamic Pot
                                </label>
                                 <label className="flex items-center gap-2 text-white cursor-pointer">
                                    <input type="radio" name="potType" value="static" checked={potType === 'static'} onChange={() => setPotType('static')} className="h-4 w-4"/>
                                    Static Pot
                                </label>
                            </div>
                        </div>

                        {potType === 'dynamic' && (
                            <div className="flex items-center gap-4 mb-4">
                                <input
                                    type="checkbox"
                                    checked={buyInEnabled}
                                    onChange={(e) => {
                                        if (e.target.checked) { setShowBuyInWarning(true); } 
                                        else { setBuyInEnabled(false); }
                                    }}
                                    id="buy-in-check" className="h-5 w-5"
                                />
                                <label htmlFor="buy-in-check" className="text-white">Enable Buy-in</label>
                                {buyInEnabled && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-white">$</span>
                                        <input type="number" value={buyInAmount} onChange={(e) => setBuyInAmount(e.target.value)} className="bg-gray-600 text-white p-2 rounded w-24"/>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {potType === 'static' && (
                             <div className="mb-4">
                                <label htmlFor="static-pot-desc" className="text-white font-bold mb-2 block">Prize Description</label>
                                <input 
                                    type="text" 
                                    id="static-pot-desc" 
                                    value={staticPotDescription} 
                                    onChange={(e) => setStaticPotDescription(e.target.value)} 
                                    placeholder="e.g., $50 Gift Card, Free Meal" 
                                    className="w-full bg-gray-600 text-white p-3 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                        )}

                        <div className="flex gap-4">
                            <button onClick={handleCreateGame} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg">Create</button>
                            <button onClick={() => setShowCreate(false)} className="bg-gray-500 hover:bg-gray-400 text-white font-bold py-2 px-4 rounded-lg">Cancel</button>
                        </div>
                    </div>
                )}

                <h3 className="text-2xl font-bold text-white mb-4">Your Games</h3>
                <div className="space-y-4">
                    {games.length > 0 ? games.map(game => (
                        <div key={game.id} className="bg-gray-700 p-4 rounded-lg flex justify-between items-center">
                            <div>
                                <p className="font-bold text-lg text-white">{game.gameName}</p>
                                <p className={`text-sm ${game.status === 'open' ? 'text-green-400' : 'text-red-400'}`}>Status: {game.status}</p>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => { setGameToShare(game); setShowEmailInviteModal(true); }} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg">Invite Players</button>
                                <button onClick={() => manageGame(game.id)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">Manage</button>
                                {game.status === 'finished' && (
                                    <button onClick={() => confirmDelete(game)} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg">Delete</button>
                                )}
                            </div>
                        </div>
                    )) : <p className="text-gray-400">You haven't created any games yet.</p>}
                </div>
                 <div className="flex justify-between items-center w-full mt-6">
                    <button onClick={() => setPage('home')} className="text-gray-400 hover:text-white">&larr; Back to Home</button>
                    <button onClick={handleLogout} className="text-gray-400 hover:text-white">Logout</button>
                </div>
            </div>
            
            {showQrCodeModal && <QrCodeModal user={user} onClose={() => setShowQrCodeModal(false)} />}
            {showShareLinkModal && <ShareLinkModal user={user} onClose={() => setShowShareLinkModal(false)} isOpen={showShareLinkModal} />}
            {showEmailInviteModal && <EmailInviteModal 
                isOpen={showEmailInviteModal}
                onClose={() => { setShowEmailInviteModal(false); setGameToShare(null); }}
                onSend={handleSendInvites}
                gameName={gameToShare?.gameName}
            />}
            {showPaymentModal && <PaymentSetupModal user={user} onClose={() => setShowPaymentModal(false)} />}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onConfirm={handleDeleteGame}
                onCancel={() => setShowDeleteModal(false)}
                message={`Are you sure you want to delete the game "${gameToDelete?.gameName}"? This action cannot be undone.`}
            />
            <ConfirmationModal
                isOpen={showBuyInWarning}
                onConfirm={() => {
                    setBuyInEnabled(true);
                    setShowBuyInWarning(false);
                }}
                onCancel={() => setShowBuyInWarning(false)}
                message={'Please verify "Social Gambling" is allowed in your Jurisdiction. We do not track your location and can not advise on applicable laws or regulations.'}
                confirmText="OK"
            />
        </>
    );
};

const PaymentSetupModal = ({ user, onClose }) => {
    const [venmo, setVenmo] = useState('');
    const [cashapp, setCashapp] = useState('');
    const [paypal, setPaypal] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        const paymentInfoRef = doc(db, `artifacts/${appId}/users/${user.uid}/paymentInfo/methods`);
        getDoc(paymentInfoRef).then(docSnap => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setVenmo(data.venmo || '');
                setCashapp(data.cashapp || '');
                setPaypal(data.paypal || '');
            }
            setLoading(false);
        });
    }, [user.uid]);

    const handleSave = async () => {
        setSaving(true);
        setMessage('');
        const paymentInfoRef = doc(db, `artifacts/${appId}/users/${user.uid}/paymentInfo/methods`);
        try {
            await setDoc(paymentInfoRef, {
                venmo: venmo.trim(),
                cashapp: cashapp.trim().replace('$', ''),
                paypal: paypal.trim()
            }, { merge: true });
            setMessage('Payment methods saved successfully!');
            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (error) {
            console.error("Error saving payment methods:", error);
            setMessage("Failed to save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border-2 border-yellow-500 p-8 rounded-lg shadow-xl w-full max-w-lg">
                <h2 className="text-2xl font-bold text-white mb-4">Setup Your Payment Methods</h2>
                <p className="text-gray-400 mb-6">Enter your usernames or links below. Players will be directed to these to pay their buy-ins.</p>
                {loading ? <p className="text-white">Loading...</p> : (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-white mb-1">Venmo Username</label>
                            <input type="text" value={venmo} onChange={(e) => setVenmo(e.target.value)} placeholder="e.g., Your-Name-123" className="w-full bg-gray-700 p-2 rounded"/>
                        </div>
                        <div>
                            <label className="block text-white mb-1">Cash App $Cashtag</label>
                            <input type="text" value={cashapp} onChange={(e) => setCashapp(e.target.value)} placeholder="e.g., $YourCashtag" className="w-full bg-gray-700 p-2 rounded"/>
                        </div>
                         <div>
                            <label className="block text-white mb-1">PayPal.me Username</label>
                            <input type="text" value={paypal} onChange={(e) => setPaypal(e.target.value)} placeholder="e.g., YourName" className="w-full bg-gray-700 p-2 rounded"/>
                             <p className="text-xs text-gray-500 mt-1">Just the username part of paypal.me/YourName</p>
                        </div>
                    </div>
                )}
                {message && <p className="text-green-400 mt-4">{message}</p>}
                <div className="flex justify-end gap-4 mt-8">
                    <button onClick={onClose} className="bg-gray-600 text-white font-bold py-2 px-4 rounded-lg">Cancel</button>
                    <button onClick={handleSave} disabled={saving || loading} className="bg-yellow-500 text-black font-bold py-2 px-4 rounded-lg disabled:bg-yellow-300">
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    )
};

const QrCodeModal = ({ user, onClose }) => {
    useEffect(() => {
        const qrCodeElement = document.getElementById('qrcode');
        if (qrCodeElement && typeof QRCode !== 'undefined') {
            qrCodeElement.innerHTML = ''; // Clear previous QR code
            const url = `https://penaltypoker.com?hostId=${user.uid}`;
            new QRCode(qrCodeElement, {
                text: url,
                width: 256,
                height: 256,
                colorDark : "#ffffff",
                colorLight : "#111827",
                correctLevel : QRCode.CorrectLevel.H
            });
        }
    }, [user]);

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border-2 border-purple-500 p-8 rounded-lg shadow-xl text-center">
                <h2 className="text-2xl font-bold text-white mb-4">Scan to Join My Games</h2>
                <div id="qrcode" className="flex justify-center p-4 bg-white rounded-lg"></div>
                <p className="text-gray-400 text-xs mt-4">Players can scan this code to see your list of active games.</p>
                <button onClick={onClose} className="mt-6 bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-6 rounded-lg">Close</button>
            </div>
        </div>
    );
};

const ShareLinkModal = ({ isOpen, onClose, user }) => {
    const [copySuccess, setCopySuccess] = useState('');

    if (!isOpen) return null;

    const shareUrl = `https://penaltypoker.com?hostId=${user.uid}`;

    const handleCopy = () => {
        navigator.clipboard.writeText(shareUrl).then(() => {
            setCopySuccess('Copied!');
            setTimeout(() => setCopySuccess(''), 2000); // Reset after 2 seconds
        }, (err) => {
            setCopySuccess('Failed!');
            console.error('Could not copy text: ', err);
        });
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border-2 border-indigo-500 p-8 rounded-lg shadow-xl text-center w-full max-w-lg">
                <h2 className="text-2xl font-bold text-white mb-4">Share Link to Your Games</h2>
                <p className="text-gray-400 mb-6">Send this link to players so they can join your games.</p>
                <div className="flex items-center bg-gray-800 border border-gray-700 rounded-lg p-2">
                    <input
                        type="text"
                        value={shareUrl}
                        readOnly
                        className="w-full bg-transparent text-gray-300 p-2 focus:outline-none"
                    />
                    <button
                        onClick={handleCopy}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg ml-2 whitespace-nowrap"
                    >
                        {copySuccess || 'Copy Link'}
                    </button>
                </div>
                <button onClick={onClose} className="mt-8 bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-lg">Close</button>
            </div>
        </div>
    );
};

const EmailInviteModal = ({ isOpen, onClose, onSend, gameName }) => {
    const [emails, setEmails] = useState('');
    const [includeContacts, setIncludeContacts] = useState(true);
    const [status, setStatus] = useState('idle'); // 'idle', 'sending', 'sent', 'error'
    const [error, setError] = useState('');

    const handleSend = async () => {
        const manualEmails = emails.split(',').map(e => e.trim()).filter(e => e);
        
        if (manualEmails.length === 0 && !includeContacts) {
            setError('Please enter at least one email or select to include contacts.');
            return;
        }
        
        const invalidEmails = manualEmails.filter(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
        if (invalidEmails.length > 0) {
            setError(`Invalid email format: ${invalidEmails.join(', ')}`);
            return;
        }

        setError('');
        setStatus('sending');
        const result = await onSend(manualEmails, includeContacts);

        if (result.success) {
            setStatus('sent');
            setTimeout(() => {
                onClose();
                setStatus('idle'); // Reset for next time
            }, 2000);
        } else {
            setStatus('error');
            setError(result.error || 'An unknown error occurred.');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border-2 border-green-500 p-8 rounded-lg shadow-xl text-center w-full max-w-lg">
                <h2 className="text-2xl font-bold text-white mb-2">Invite Players</h2>
                <p className="text-gray-400 mb-6">Invite players to "{gameName}".</p>
                
                {status !== 'sent' && (
                    <>
                        <textarea
                            value={emails}
                            onChange={(e) => setEmails(e.target.value)}
                            placeholder="player1@example.com, player2@example.com"
                            className="w-full bg-gray-800 text-white p-3 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-green-500 h-24"
                            disabled={status === 'sending'}
                        />

                        <div className="flex items-center justify-center my-4">
                            <input
                                type="checkbox"
                                id="include-contacts"
                                checked={includeContacts}
                                onChange={(e) => setIncludeContacts(e.target.checked)}
                                className="h-6 w-6 rounded"
                                disabled={status === 'sending'}
                            />
                            <label htmlFor="include-contacts" className="ml-3 text-lg text-white">
                                Include my saved contacts
                            </label>
                        </div>

                        {error && <p className="bg-red-500 text-white p-2 rounded my-4 text-sm">{error}</p>}

                        <div className="flex justify-center gap-4">
                            <button onClick={handleSend} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg" disabled={status === 'sending'}>
                                {status === 'sending' ? 'Sending...' : 'Send Invites'}
                            </button>
                            <button onClick={onClose} className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-lg" disabled={status === 'sending'}>
                                Skip
                            </button>
                        </div>
                    </>
                )}

                {status === 'sent' && (
                    <p className="text-green-400 text-xl">Invites sent successfully!</p>
                )}
            </div>
        </div>
    );
};


const ConfirmationModal = ({ isOpen, onConfirm, onCancel, message, confirmText = "Confirm" }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-6 rounded-lg shadow-xl text-center">
                <p className="text-white text-lg mb-4">{message}</p>
                <div className="flex justify-center gap-4">
                    <button onClick={onConfirm} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg">{confirmText}</button>
                    {confirmText === "Confirm" && <button onClick={onCancel} className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-lg">Cancel</button>}
                </div>
            </div>
        </div>
    );
};

const PendingPlayersModal = ({ isOpen, onClose, pendingPlayers, gameId, currentHand, game }) => {
    const [playerSettings, setPlayerSettings] = useState({});
    const [error, setError] = useState('');
    const [playerToReject, setPlayerToReject] = useState(null);

    const buyInEnabled = game?.potType === 'dynamic' && game?.buyInEnabled;
    
    useEffect(() => {
        if (isOpen) {
            const initialSettings = {};
            pendingPlayers.forEach(p => {
                initialSettings[p.uid] = {
                    quarters: [1, 2, 3, 4].filter(q => q >= currentHand),
                    buyInExempt: false
                };
            });
            setPlayerSettings(initialSettings);
        }
    }, [isOpen, pendingPlayers, currentHand]);

    const handleSettingChange = (uid, type, value) => {
        setPlayerSettings(prev => {
            const newSettings = { ...prev[uid] };
            if (type === 'quarter') {
                const q = Number(value);
                if (newSettings.quarters.includes(q)) {
                    newSettings.quarters = newSettings.quarters.filter(cq => cq !== q);
                } else {
                    newSettings.quarters = [...newSettings.quarters, q].sort();
                }
            } else if (type === 'exempt') {
                newSettings.buyInExempt = value;
            }
            return { ...prev, [uid]: newSettings };
        });
    };

    const handleApprovePlayer = async (playerToApprove) => {
        const gameRef = doc(db, `artifacts/${appId}/public/data/games`, gameId);
        const settings = playerSettings[playerToApprove.uid];
        try {
            await runTransaction(db, async (transaction) => {
                const gameDoc = await transaction.get(gameRef);
                if (!gameDoc.exists()) throw new Error("Game not found.");
                const gameData = gameDoc.data();
                const pending = gameData.pendingPlayers.filter(p => p.uid !== playerToApprove.uid);
                const approved = gameData.approvedPlayers || {};

                if (!approved[playerToApprove.uid]) {
                    approved[playerToApprove.uid] = {
                        username: playerToApprove.username,
                        cards: [],
                        handDetails: null,
                        activeQuarters: settings.quarters,
                        buyInExempt: settings.buyInExempt,
                        isJoiningNextHand: gameData.status === 'locked'
                    };
                }
                transaction.update(gameRef, { pendingPlayers: pending, approvedPlayers: approved });
            });

            if (playerToApprove.email) {
                const contactRef = doc(db, `artifacts/${appId}/users/${game.hostId}/contacts`, playerToApprove.uid);
                await setDoc(contactRef, {
                    username: playerToApprove.username,
                    email: playerToApprove.email,
                    sharedAt: serverTimestamp()
                }, { merge: true });
            }

        } catch (e) {
            console.error(e);
            setError("Failed to approve player. " + e.message);
        }
    };

    const handleRejectPlayer = async () => {
        if (!playerToReject) return;
        const gameRef = doc(db, `artifacts/${appId}/public/data/games`, gameId);
        try {
            await runTransaction(db, async (transaction) => {
                const gameDoc = await transaction.get(gameRef);
                if (!gameDoc.exists()) throw new Error("Game not found.");
                const gameData = gameDoc.data();
                const pending = gameData.pendingPlayers.filter(p => p.uid !== playerToReject.uid);
                transaction.update(gameRef, { pendingPlayers: pending });
            });
        } catch (e) {
            console.error(e);
            setError("Failed to reject player. " + e.message);
        } finally {
            setPlayerToReject(null);
        }
    };
    
    const getPaymentStatusText = (status) => {
        const statusMap = {
            'paid_venmo': '(Paid with Venmo)',
            'paid_cashapp': '(Paid with Cash App)',
            'paid_paypal': '(Paid with PayPal)',
            'in_person': '(Paying in Person)',
        };
        return statusMap[status] || '';
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                <div className="bg-gray-900 border-2 border-yellow-500 p-6 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                    <h2 className="text-2xl font-bold text-yellow-400 mb-4">Manage Pending Players</h2>
                    {error && <p className="bg-red-500 text-white p-2 rounded mb-4 text-sm">{error}</p>}
                    <div className="space-y-3">
                        {pendingPlayers.length > 0 ? pendingPlayers.map(p => (
                            <div key={p.uid} className="bg-gray-700 p-4 rounded-lg">
                                <div className="flex justify-between items-center">
                                    <span className="text-white text-lg font-bold">
                                        {p.username}
                                        <span className="text-xs font-bold text-yellow-400 ml-2">{getPaymentStatusText(p.paymentStatus)}</span>
                                    </span>
                                    <div className="flex gap-4">
                                        <button onClick={() => handleApprovePlayer(p)} className="bg-green-500 hover:bg-green-600 text-white font-bold py-1 px-4 rounded-lg">Approve</button>
                                        <button onClick={() => setPlayerToReject(p)} className="bg-red-600 hover:bg-red-700 text-white font-bold py-1 px-4 rounded-lg">Reject</button>
                                    </div>
                                </div>
                                {buyInEnabled && playerSettings[p.uid] && (
                                    <div className="mt-3 pt-3 border-t border-gray-600">
                                        <div className="flex items-center gap-4 text-xs text-white">
                                            <span className="font-bold">Quarters:</span>
                                            {[1, 2, 3, 4].map(q => {
                                                if (q < currentHand) return null;
                                                return (
                                                    <div key={q} className="flex items-center gap-1">
                                                        <input
                                                            type="checkbox"
                                                            id={`q${q}-${p.uid}`}
                                                            checked={playerSettings[p.uid]?.quarters.includes(q)}
                                                            onChange={(e) => handleSettingChange(p.uid, 'quarter', e.target.value)}
                                                            value={q}
                                                            className="h-4 w-4"
                                                        />
                                                        <label htmlFor={`q${q}-${p.uid}`}>Q{q}</label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-white mt-2">
                                            <input
                                                type="checkbox"
                                                id={`exempt-${p.uid}`}
                                                checked={playerSettings[p.uid]?.buyInExempt}
                                                onChange={(e) => handleSettingChange(p.uid, 'exempt', e.target.checked)}
                                                className="h-4 w-4"
                                            />
                                            <label htmlFor={`exempt-${p.uid}`}>Exempt from Buy-in</label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )) : <p className="text-gray-400">No players waiting for approval.</p>}
                    </div>
                    <div className="text-center mt-6">
                        <button onClick={onClose} className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-lg">Close</button>
                    </div>
                </div>
            </div>
            <ConfirmationModal
                isOpen={!!playerToReject}
                onConfirm={handleRejectPlayer}
                onCancel={() => setPlayerToReject(null)}
                message={`Are you sure you want to reject ${playerToReject?.username}? They will be removed from the pending list.`}
            />
        </>
    );
};

const EditPlayerModal = ({ isOpen, onClose, player, gameId, currentHand }) => {
    const [activeQuarters, setActiveQuarters] = useState([]);
    const [buyInExempt, setBuyInExempt] = useState(false);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (player) {
            setActiveQuarters(player.activeQuarters || []);
            setBuyInExempt(player.buyInExempt || false);
        }
    }, [player]);

    const handleQuarterChange = (q) => {
        setActiveQuarters(prev =>
            prev.includes(q) ? prev.filter(item => item !== q) : [...prev, q]
        );
    };

    const handleSave = async () => {
        if (!player) return;
        setSaving(true);
        setError('');
        const gameRef = doc(db, `artifacts/${appId}/public/data/games`, gameId);
        try {
            await updateDoc(gameRef, {
                [`approvedPlayers.${player.uid}.activeQuarters`]: activeQuarters,
                [`approvedPlayers.${player.uid}.buyInExempt`]: buyInExempt
            });
            onClose();
        } catch (e) {
            console.error("Error updating player:", e);
            setError("Failed to save changes. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen || !player) return null;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border-2 border-blue-500 p-6 rounded-lg shadow-xl w-full max-w-lg">
                <h2 className="text-2xl font-bold text-blue-400 mb-4">Edit Player: {player.username}</h2>
                {error && <p className="bg-red-500 text-white p-2 rounded mb-4 text-sm">{error}</p>}
                
                <div className="mt-3 pt-3">
                    <div className="flex items-center gap-4 text-sm text-white mb-4">
                        <span className="font-bold">Active Quarters:</span>
                        {[1, 2, 3, 4].map(q => {
                            const disabled = q < currentHand;
                            return (
                                <div key={q} className={`flex items-center gap-1 ${disabled ? 'opacity-50' : ''}`}>
                                    <input
                                        type="checkbox"
                                        id={`edit-q${q}-${player.uid}`}
                                        checked={activeQuarters.includes(q)}
                                        onChange={() => handleQuarterChange(q)}
                                        disabled={disabled}
                                        className="h-4 w-4"
                                    />
                                    <label htmlFor={`edit-q${q}-${player.uid}`}>Q{q}</label>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-white mt-2">
                        <input
                            type="checkbox"
                            id={`edit-exempt-${player.uid}`}
                            checked={buyInExempt}
                            onChange={(e) => setBuyInExempt(e.target.checked)}
                            className="h-4 w-4"
                        />
                        <label htmlFor={`edit-exempt-${player.uid}`}>Exempt from Buy-in</label>
                    </div>
                </div>

                <div className="flex justify-end gap-4 mt-8">
                    <button onClick={onClose} className="bg-gray-600 text-white font-bold py-2 px-4 rounded-lg">Cancel</button>
                    <button onClick={handleSave} disabled={saving} className="bg-blue-500 text-white font-bold py-2 px-4 rounded-lg disabled:bg-blue-300">
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const GameManagementPage = ({ setPage, gameId, user }) => {
    const [game, setGame] = useState(null);
    const [error, setError] = useState('');
    const [commentaries, setCommentaries] = useState({});
    const [loadingCommentary, setLoadingCommentary] = useState(null);
    const [isEndingHand, setIsEndingHand] = useState(false);
    const [showLockHandModal, setShowLockHandModal] = useState(false);
    const [showDealModal, setShowDealModal] = useState(false);
    const [showPendingPlayersModal, setShowPendingPlayersModal] = useState(false);
    const [playerToRemove, setPlayerToRemove] = useState(null);
    const [playerToEdit, setPlayerToEdit] = useState(null);

    useEffect(() => {
        if (!gameId) return;
        const gameRef = doc(db, `artifacts/${appId}/public/data/games`, gameId);
        const unsubscribe = onSnapshot(gameRef, (doc) => {
            if (doc.exists()) {
                setGame({ id: doc.id, ...doc.data() });
            } else { setError("Game not found."); }
        });
        return () => unsubscribe();
    }, [gameId]);

    const handleGetCommentary = async (hand) => {
        setLoadingCommentary(hand.hand);
        const prompt = `In the voice of an excited football sportscaster, give a short, dramatic commentary for a poker game. Player "${hand.winner}" just won hand #${hand.hand} with a "${hand.winningHand}".`;
        const commentary = await callGeminiAPI(prompt);
        setCommentaries(prev => ({...prev, [hand.hand]: commentary}));
        setLoadingCommentary(null);
    };

    const handleLockHand = async () => {
        setShowLockHandModal(false);
        const gameRef = doc(db, `artifacts/${appId}/public/data/games`, gameId);
        try {
            await runTransaction(db, async (transaction) => {
                const gameDoc = await transaction.get(gameRef);
                if (!gameDoc.exists()) throw new Error("Game not found.");
                let approved = { ...gameDoc.data().approvedPlayers };
                Object.keys(approved).forEach(uid => {
                    approved[uid].cards = [];
                    approved[uid].handDetails = null;
                });
                transaction.update(gameRef, { status: 'locked', lastWinner: null, approvedPlayers: approved });
            });
        } catch (e) {
            console.error("Error locking hand:", e);
            setError(e.toString());
        }
    };

    const handleDealCard = async () => {
        setShowDealModal(false);
        const gameRef = doc(db, `artifacts/${appId}/public/data/games`, gameId);
        try {
            await runTransaction(db, async (transaction) => {
                const gameDoc = await transaction.get(gameRef);
                if (!gameDoc.exists()) throw new Error("Game not found.");
                const gameData = gameDoc.data();
                let deck = gameData.deck || createDeck();
                let approved = { ...gameData.approvedPlayers };
                const activePlayerUIDs = Object.keys(approved).filter(uid => !approved[uid].isJoiningNextHand);
                if (deck.length < activePlayerUIDs.length) throw new Error("Not enough cards in the deck!");
                activePlayerUIDs.forEach(uid => {
                    if (approved[uid].cards.length < 10) {
                        approved[uid].cards.push(deck.pop());
                    }
                });
                transaction.update(gameRef, { approvedPlayers: approved, deck: deck });
            });
        } catch (e) { console.error(e); setError(e.toString()); }
    };
    
    // Revised End Hand Sequence
    const handleEndHandSequence = async () => {
        setIsEndingHand(true);
        const gameRef = doc(db, `artifacts/${appId}/public/data/games`, gameId);
        const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));
        const DEAL_DELAY = 10000; // 10 seconds

        const dealRoundOfCards = async () => {
             try {
                await runTransaction(db, async (transaction) => {
                    const freshDoc = await transaction.get(gameRef);
                    if (!freshDoc.exists()) throw new Error("Game disappeared during deal round.");
                    const data = freshDoc.data();
                    const deck = data.deck;
                    const approved = { ...data.approvedPlayers };
                    // Only deal to active players who need cards
                    const activePlayerUIDs = Object.keys(approved).filter(uid => 
                        !approved[uid].isJoiningNextHand && approved[uid].cards.length < 5
                    ); 
                    if (deck.length < activePlayerUIDs.length) throw new Error("Not enough cards to deal a full round.");
                    activePlayerUIDs.forEach(uid => {
                        approved[uid].cards.push(deck.pop());
                    });
                    transaction.update(gameRef, { deck, approvedPlayers: approved });
                });
                return true; // Indicate success
            } catch (e) {
                console.error("Error in dealRoundOfCards:", e);
                setError(`Error dealing cards: ${e.message}`);
                return false; // Indicate failure
            }
        };
    
        const finalizeHand = async (finalGameData) => {
             try {
                await runTransaction(db, async (transaction) => {
                    let approved = { ...finalGameData.approvedPlayers };
                    let playerHandHistories = finalGameData.playerHandHistories || {};
                    let winner = null;
                    let bestHandResult = { rank: -1, name: 'No Hand', values: [], best5CardHand: [] };

                    Object.entries(approved).forEach(([uid, player]) => {
                       if (!player.isJoiningNextHand) {
                            const playerBestHand = evaluateBestHand(player.cards);
                            player.handDetails = playerBestHand; // Store details for display
                            if (!playerHandHistories[uid]) playerHandHistories[uid] = [];
                            playerHandHistories[uid].push({ hand: finalGameData.currentHand || 1, cards: playerBestHand.best5CardHand, handName: playerBestHand.name });
    
                            // Determine winner logic
                            if (playerBestHand.rank > bestHandResult.rank) {
                                bestHandResult = playerBestHand;
                                winner = { ...player, uid };
                            } else if (playerBestHand.rank === bestHandResult.rank) {
                                for (let i = 0; i < playerBestHand.values.length; i++) {
                                    if (playerBestHand.values[i] > bestHandResult.values[i]) {
                                        bestHandResult = playerBestHand;
                                        winner = { ...player, uid };
                                        break;
                                    }
                                    if (playerBestHand.values[i] < bestHandResult.values[i]) break;
                                }
                            }
                        }
                    });
    
                    Object.keys(approved).forEach(uid => { if (approved[uid].isJoiningNextHand) approved[uid].isJoiningNextHand = false; });
                    const handWinners = finalGameData.handWinners || [];
                    if (winner) handWinners.push({ hand: finalGameData.currentHand || 1, winner: winner.username, winningHand: bestHandResult.name, cards: bestHandResult.best5CardHand });
                    const lastWinnerInfo = winner ? { username: winner.username, handName: bestHandResult.name, cards: bestHandResult.best5CardHand } : null;
                    const nextHand = (finalGameData.currentHand || 1) + 1;
                    const newStatus = nextHand > 4 ? 'finished' : 'open';
                    transaction.update(gameRef, { approvedPlayers: approved, handWinners, playerHandHistories, lastWinner: lastWinnerInfo, currentHand: nextHand, status: newStatus });
                });
            } catch(e) {
                 console.error("Error in finalizeHand:", e);
                 setError(`Error finalizing hand: ${e.message}`);
            }
        };
    
        try {
            let gameSnap = await getDoc(gameRef);
            if (!gameSnap.exists()) throw new Error("Game not found.");
            
            let gameData = gameSnap.data();
            let activePlayers = Object.values(gameData.approvedPlayers).filter(p => !p.isJoiningNextHand);
            let needsCards = activePlayers.some(p => p.cards.length < 5);
    
            if (!needsCards) {
                // Everyone has enough cards, finalize immediately
                await finalizeHand(gameData);
                setIsEndingHand(false);
                return;
            }
    
            // If we are here, cards are needed.
            while (true) {
                const success = await dealRoundOfCards();
                if (!success) break; // Stop if dealing failed
    
                gameSnap = await getDoc(gameRef); // Get fresh data after deal
                gameData = gameSnap.data();
                activePlayers = Object.values(gameData.approvedPlayers).filter(p => !p.isJoiningNextHand);
                needsCards = activePlayers.some(p => p.cards.length < 5);
                
                await wait(DEAL_DELAY); // Wait 10 seconds *after* dealing
    
                if (!needsCards) {
                    break; // Exit loop, ready to finalize
                }
                 // If more cards are needed, the loop continues and will wait again *after* the next deal
            }
    
            await finalizeHand(gameData);
    
        } catch (e) {
            console.error("Error in end hand sequence:", e);
            setError(e.message);
        } finally {
            setIsEndingHand(false);
        }
    };

    const handleRemovePlayer = async () => {
        if (!playerToRemove) return;
        const gameRef = doc(db, `artifacts/${appId}/public/data/games`, gameId);
        try {
            await runTransaction(db, async (transaction) => {
                const gameDoc = await transaction.get(gameRef);
                if (!gameDoc.exists()) throw new Error("Game not found.");
                const gameData = gameDoc.data();
                const approved = { ...gameData.approvedPlayers };
                delete approved[playerToRemove.uid];
                transaction.update(gameRef, { approvedPlayers: approved });
            });
        } catch (e) {
            console.error("Error removing player:", e);
            setError("Failed to remove player.");
        } finally {
            setPlayerToRemove(null);
        }
    };

    if (!game) return <div className="text-white">Loading Game...</div>;

    const approvedPlayersList = Object.entries(game.approvedPlayers || []).map(([uid, data]) => ({ uid, ...data }));
    const isGameOver = game.status === 'finished';
    const currentHandForDisplay = game.currentHand || 1;
    
    let potContent;
    if (game.potType === 'static') {
        potContent = <p className="text-3xl font-bold text-yellow-400">{game.staticPotDescription}</p>;
    } else {
        const payingPlayers = approvedPlayersList.filter(p => !p.isJoiningNextHand && p.activeQuarters && !p.buyInExempt && p.activeQuarters.includes(currentHandForDisplay)).length;
        const potTotal = game.buyInEnabled ? payingPlayers * game.buyInAmount : 0;
        potContent = <p className="text-3xl font-bold text-green-400">${potTotal}</p>;
    }

    return (
        <>
            <div className="w-full max-w-6xl bg-gray-800/80 backdrop-blur-sm p-8 rounded-lg shadow-2xl">
                <div className="flex justify-between items-start mb-6 border-b border-gray-700 pb-4">
                    <div>
                        <h2 className="text-3xl font-bold text-white mb-2">{game.gameName}</h2>
                        <p className={`text-lg ${game.status === 'open' ? 'text-green-400' : 'text-red-400'}`}>
                            Status: {game.status}
                            {` | Quarter: ${currentHandForDisplay > 4 ? 4 : currentHandForDisplay}/4`}
                        </p>
                         {game.buyInEnabled && <p className="text-lg text-green-400 font-bold">${game.buyInAmount} Buy-in</p>}
                    </div>
                    <div className="text-right">
                        <h3 className="text-lg font-bold text-gray-400">PLAYERS</h3>
                        <p className="text-5xl font-bold text-white">{approvedPlayersList.filter(p => !p.isJoiningNextHand).length}</p>
                        <h3 className="text-lg font-bold text-gray-400 mt-2">CURRENT POT</h3>
                        {potContent}
                    </div>
                </div>

                {error && <p className="bg-red-500 text-white p-2 rounded mb-4">{error}</p>}

                {game.lastWinner && (
                    <div className="bg-yellow-500 text-black p-4 rounded-lg mb-6 text-center">
                        <h4 className="font-bold text-xl">Quarter {game.currentHand - 1} Winner: {game.lastWinner.username} with a {game.lastWinner.handName}!</h4>
                        <div className="flex justify-center gap-2 mt-2 font-mono">
                            {game.lastWinner.cards && game.lastWinner.cards.map(c => (
                                <span key={c.id || c.image} className={`font-bold ${c.suit === '♥' || c.suit === '♦' ? 'text-red-700' : 'text-gray-800'}`}>
                                    {c.value}{c.suit}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                     <div>
                        <h3 className="text-2xl font-bold text-white mb-4">Player Management</h3>
                        <button
                            onClick={() => setShowPendingPlayersModal(true)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg w-full"
                        >
                            Manage Pending Players ({game.pendingPlayers?.length || 0})
                        </button>
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-white mb-4">Approved Players ({approvedPlayersList.length})</h3>
                        <div className="space-y-4 max-h-60 overflow-y-auto pr-2">
                            {approvedPlayersList.length > 0 ? approvedPlayersList.map(p => {
                                const sortedCards = sortPlayerHand(p.cards || []);
                                return (
                                <div key={p.uid} className="bg-gray-900 p-3 rounded-lg text-white">
                                    <div className="flex justify-between items-center">
                                        <p className="font-bold">{p.username} {p.isJoiningNextHand && <span className="text-xs text-yellow-400">(Joining next hand)</span>}</p>
                                        <div className="flex gap-2">
                                            {game.buyInEnabled && <button onClick={() => setPlayerToEdit(p)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-1 px-3 rounded">Edit</button>}
                                            <button onClick={() => setPlayerToRemove(p)} className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-1 px-3 rounded">Remove</button>
                                        </div>
                                    </div>
                                    {game.buyInEnabled && (
                                        <div className="text-xs text-yellow-400 mt-1">
                                            Quarters: {p.activeQuarters?.join(', ')} {p.buyInExempt && '(Exempt)'}
                                        </div>
                                    )}
                                    <div className="flex gap-2 mt-2 flex-wrap font-mono">
                                        {sortedCards.map(c => (
                                            <span key={c.id} className={`font-bold text-sm ${c.suit === '♥' || c.suit === '♦' ? 'text-red-400' : 'text-gray-200'}`}>
                                                {c.value}{c.suit}
                                            </span>
                                        ))}
                                    </div>
                                    {p.handDetails && p.handDetails.name !== 'Invalid Hand' && (
                                        <p className="text-xs text-cyan-300 mt-1">{p.handDetails.name}</p>
                                    )}
                                </div>
                                );
                            }) : <p className="text-gray-400">No players approved.</p>}
                        </div>
                    </div>
                </div>

                {!isGameOver && (
                    <div className="flex gap-4">
                        {game.status === 'open' ? (
                             <button onClick={() => setShowLockHandModal(true)} className="bg-blue-800 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg" disabled={isEndingHand}>Lock Players & Start Hand</button>
                        ) : (
                             <button onClick={() => setShowDealModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg" disabled={isEndingHand}>Deal Next Card</button>
                        )}
                        <button onClick={handleEndHandSequence} className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-6 rounded-lg shadow-lg" disabled={isEndingHand}>
                             {isEndingHand ? 'Ending Hand...' : 'End Hand & Determine Winner'}
                        </button>
                    </div>
                )}

                {game.handWinners && game.handWinners.length > 0 && (
                    <div className="mt-8">
                        <h3 className="text-2xl font-bold text-white mb-4">Game History</h3>
                        <ul className="space-y-4 text-gray-300">
                            {game.handWinners.map(h => (
                                <li key={h.hand} className="bg-gray-900 p-4 rounded-lg">
                                    <p>Quarter {h.hand}: <strong>{h.winner}</strong> won with a {h.winningHand}.</p>
                                    <div className="flex gap-1 mt-2">
                                        {h.cards && h.cards.map(c => (
                                            <div key={c.id} className="bg-white p-1 rounded-sm shadow-md">
                                                <img src={c.image} alt={`${c.value} of ${c.suit}`} className="w-10 h-auto rounded-sm" />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-3">
                                        {commentaries[h.hand] ? (
                                            <p className="text-sm italic text-cyan-300 p-2 bg-cyan-900/50 rounded">{commentaries[h.hand]}</p>
                                        ) : (
                                            <button onClick={() => handleGetCommentary(h)} disabled={loadingCommentary === h.hand} className="text-xs bg-purple-600 hover:bg-purple-700 text-white font-bold py-1 px-2 rounded disabled:bg-purple-400">
                                                {loadingCommentary === h.hand ? '...' : '✨ Get Commentary'}
                                            </button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <button onClick={() => setPage('hostDashboard')} className="mt-8 text-gray-400 hover:text-white">&larr; Back to Dashboard</button>
            </div>
            <PendingPlayersModal
                isOpen={showPendingPlayersModal}
                onClose={() => setShowPendingPlayersModal(false)}
                pendingPlayers={game.pendingPlayers || []}
                gameId={gameId}
                currentHand={currentHandForDisplay}
                game={game}
            />
            <EditPlayerModal
                isOpen={!!playerToEdit}
                onClose={() => setPlayerToEdit(null)}
                player={playerToEdit}
                gameId={gameId}
                currentHand={currentHandForDisplay}
            />
            <ConfirmationModal
                isOpen={showLockHandModal}
                onConfirm={handleLockHand}
                onCancel={() => setShowLockHandModal(false)}
                message="Are you sure you want to lock players and start the hand? This will also clear all current hands."
            />
             <ConfirmationModal
                isOpen={showDealModal}
                onConfirm={handleDealCard}
                onCancel={() => setShowDealModal(false)}
                message="Are you sure you want to deal the next card?"
            />
            <ConfirmationModal
                isOpen={!!playerToRemove}
                onConfirm={handleRemovePlayer}
                onCancel={() => setPlayerToRemove(null)}
                message={`Are you sure you want to remove ${playerToRemove?.username} from the game?`}
            />
        </>
    );
};

const ContactInfoModal = ({ isOpen, onClose, onSubmit }) => {
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');

    const handleSubmit = () => {
        onSubmit({ email, phone });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm">
                <h3 className="text-xl font-bold text-white mb-4 text-center">Share Contact Info</h3>
                <p className="text-gray-400 text-sm mb-4 text-center">The host would like to contact you for future games. This is optional.</p>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email Address"
                    className="w-full bg-gray-700 text-white p-3 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Phone Number (Optional)"
                    className="w-full bg-gray-700 text-white p-3 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex justify-end gap-4">
                    <button type="button" onClick={onClose} className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-lg">Skip</button>
                    <button type="button" onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg">Submit</button>
                </div>
            </div>
        </div>
    );
};

const GamePage = ({ setPage, user, gameId }) => {
    const [game, setGame] = useState(null);
    const [error, setError] = useState('');
    const [showHandsModal, setShowHandsModal] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [hasSharedContact, setHasSharedContact] = useState(false);
    const [commentaries, setCommentaries] = useState({});
    const [loadingCommentary, setLoadingCommentary] = useState(null);
    const [isMuted, setIsMuted] = useState(false);

    const playerData = game?.approvedPlayers?.[user?.uid];
    const isPending = game?.pendingPlayers?.some(p => p.uid === user?.uid);
    const isGameOver = game?.status === 'finished';

    const prevCardCount = useRef(null);
    const prevHand = useRef(null);
    const wakeLock = useRef(null);

    // --- Screen Wake Lock Effect ---
    useEffect(() => {
        const acquireWakeLock = async () => {
            if ('wakeLock' in navigator) {
                try {
                    wakeLock.current = await navigator.wakeLock.request('screen');
                    // console.log('Screen Wake Lock is active.'); // Optional: Keep for debugging
                    wakeLock.current.addEventListener('release', () => {
                         // console.log('Screen Wake Lock was released.'); // Optional: Keep for debugging
                    });
                } catch (err) {
                    console.error(`${err.name}, ${err.message}`);
                }
            } else {
                console.log('Wake Lock API not supported.');
            }
        };

        acquireWakeLock();

        const handleVisibilityChange = () => {
            if (wakeLock.current !== null && document.visibilityState === 'visible') {
                acquireWakeLock();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            if (wakeLock.current !== null) {
                wakeLock.current.release();
                wakeLock.current = null;
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // --- Main Game Data Listener ---
    useEffect(() => {
        if (!gameId) return;
        const gameRef = doc(db, `artifacts/${appId}/public/data/games`, gameId);
        const unsubscribe = onSnapshot(gameRef, (doc) => {
            if (doc.exists()) {
                const gameData = { id: doc.id, ...doc.data() };
                setGame(gameData);

                if (gameData.hostId && user?.uid) {
                    const contactRef = doc(db, `artifacts/${appId}/users/${gameData.hostId}/contacts`, user.uid);
                    getDoc(contactRef).then(contactSnap => {
                        if (contactSnap.exists()) {
                            setHasSharedContact(true);
                        }
                    });
                }
            } else {
                setError("Game not found.");
                setPage('activeGames');
            }
        });
        return () => unsubscribe();
    }, [gameId, setPage, user?.uid]);
    
    // --- Audio Cue Effects ---
    useEffect(() => {
        // Guard against running this effect before playerData is loaded
        if (!playerData || !playerData.cards) {
            return;
        }

        const currentCardCount = playerData.cards.length;
        const currentHand = game?.currentHand || 1;

        // Initialize refs on first render
        if (prevCardCount.current === null || prevHand.current === null) {
            prevCardCount.current = currentCardCount;
            prevHand.current = currentHand;
            return;
        }
        
        if (!isMuted) {
            // Play whistle only if cards were added
            if (currentCardCount > prevCardCount.current && game?.status === 'locked') {
                playSound('whistle');
            }
            // Play horn only if the hand/quarter advanced
            if (currentHand > prevHand.current) {
                playSound('horn');
            }
        }
        
        // Update refs for the next render
        prevCardCount.current = currentCardCount;
        prevHand.current = currentHand;

    }, [playerData?.cards, game?.currentHand, game?.status, isMuted, playerData]); // Added playerData to deps


    const handleGetCommentary = async (hand) => {
        setLoadingCommentary(hand.hand);
        const prompt = `In the voice of an excited football sportscaster, give a short, dramatic commentary for a poker game. Player "${hand.winner}" just won hand #${hand.hand} with a "${hand.winningHand}".`;
        const commentary = await callGeminiAPI(prompt);
        setCommentaries(prev => ({...prev, [hand.hand]: commentary}));
        setLoadingCommentary(null);
    };

    const handleShareContactInfo = async ({ email, phone }) => {
        if (!game.hostId || !user.uid || !playerData.username) {
            setShowContactModal(false);
            return;
        }

        if (email || phone) {
            const contactRef = doc(db, `artifacts/${appId}/users/${game.hostId}/contacts`, user.uid);
            try {
                await setDoc(contactRef, {
                    username: playerData.username,
                    email: email || null,
                    phone: phone || null,
                    sharedAt: serverTimestamp()
                }, { merge: true });
                setHasSharedContact(true);
            } catch (e) {
                console.error("Error sharing contact info:", e);
                setError("Could not save contact info.");
            }
        }
        setShowContactModal(false);
    };

    if (!game) return <div className="text-white">Loading Game...</div>;

    const myHandHistory = game.playerHandHistories?.[user.uid] || [];
    
    // Ensure playerData exists before accessing cards
    const myCards = playerData?.cards || [];
    const myBestHandDetails = myCards.length > 0 ? evaluateBestHand(myCards) : null;
    const best5CardIds = myBestHandDetails ? new Set(myBestHandDetails.best5CardHand.map(c => c.id)) : new Set();
    const sortedHand = sortPlayerHand(myCards);


    const Card = ({ card, facedown, isBestHand }) => (
        <div className={`relative w-16 h-24 sm:w-24 sm:h-36 rounded-lg shadow-md bg-white transition-all duration-300 ${isBestHand ? 'ring-4 ring-yellow-400 scale-105' : 'ring-1 ring-gray-500'}`}>
            {facedown ? (
                <div className="w-full h-full bg-blue-900 rounded-lg border-2 border-blue-500 flex items-center justify-center">
                    <span className="text-blue-400 font-bold text-lg">PP</span>
                </div>
            ) : (
                <img
                    src={card.image}
                    alt={`${card.value} of ${card.suit}`}
                    className="w-full h-full object-cover rounded-lg"
                />
            )}
        </div>
    );

    if (!playerData && isPending) {
        return (
            <div className="text-center bg-gray-800 p-8 rounded-lg">
                <h2 className="text-2xl font-bold text-white mb-4">Waiting for Host</h2>
                <p className="text-gray-300">You have requested to join "{game.gameName}". Please wait for the host to approve you.</p>
                {error && <p className="mt-4 bg-red-500 text-white p-2 rounded">{error}</p>}
                 <button onClick={() => setPage('activeGames')} className="mt-8 text-gray-400 hover:text-white">&larr; Back to Game Lobby</button>
            </div>
        );
    }

    if (!playerData && !isPending) {
         return (
            <div className="text-center bg-gray-800 p-8 rounded-lg">
                <h2 className="text-2xl font-bold text-white mb-4">Error</h2>
                <p className="text-gray-300">You are not part of this game. You may have been removed by the host or the game has ended.</p>
                <button onClick={() => setPage('activeGames')} className="mt-8 text-gray-400 hover:text-white">&larr; Back to Game Lobby</button>
            </div>
        );
    }

    const currentHandForDisplay = game.currentHand || 1;
    
    let potContent;
    if (game.potType === 'static') {
        potContent = <p className="text-3xl font-bold text-yellow-400">{game.staticPotDescription}</p>;
    } else {
        const payingPlayers = Object.values(game.approvedPlayers).filter(p => p.activeQuarters && !p.buyInExempt && p.activeQuarters.includes(currentHandForDisplay)).length;
        const potTotal = game.buyInEnabled ? payingPlayers * game.buyInAmount : 0;
        potContent = <p className="text-3xl font-bold text-green-400">${potTotal}</p>;
    }

    const rankedPlayers = Object.entries(game.approvedPlayers)
        .map(([uid, player]) => ({ ...player, uid, handDetails: evaluateBestHand(player.cards || []) })) // Added null check for player.cards
        .sort((a, b) => {
            if (b.handDetails.rank !== a.handDetails.rank) {
                return b.handDetails.rank - a.handDetails.rank;
            }
            for (let i = 0; i < (a.handDetails.values?.length || 0) ; i++) { // Added null check for values
                 if (!b.handDetails.values || !a.handDetails.values) return 0; // Guard against undefined values
                if (b.handDetails.values[i] !== a.handDetails.values[i]) {
                    return b.handDetails.values[i] - a.handDetails.values[i];
                }
            }
            return 0;
        });

    return (
        <>
            <div className="w-full max-w-5xl bg-gray-800/80 backdrop-blur-sm p-4 sm:p-8 rounded-lg shadow-2xl">
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-3xl font-bold text-white mb-2">Penalty Poker</h2>
                        <p className="text-xl text-yellow-400 mb-6">Player: {playerData.username}</p>
                    </div>
                    <div className="text-right">
                        <h3 className="text-lg font-bold text-gray-400">CURRENT POT</h3>
                        {potContent}
                    </div>
                </div>

                {game.lastWinner && !isGameOver && (
                     <div className="bg-yellow-500 text-black p-4 rounded-lg mb-6 text-center">
                        <h4 className="font-bold text-xl">Quarter {game.currentHand - 1} Winner: {game.lastWinner.username} with a {game.lastWinner.handName}!</h4>
                        <div className="flex justify-center gap-2 mt-2 font-mono">
                            {game.lastWinner.cards && game.lastWinner.cards.map(c => (
                                <span key={c.id || c.image} className={`font-bold ${c.suit === '♥' || c.suit === '♦' ? 'text-red-700' : 'text-gray-800'}`}>
                                    {c.value}{c.suit}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {isGameOver && (
                    <div className="bg-green-700 text-white p-4 rounded-lg mb-6 text-center">
                        <h4 className="font-bold text-2xl">Game Over!</h4>
                        <p className="mt-2">Thanks for playing! The final winner was {game.lastWinner?.username}.</p>
                        {!hasSharedContact && (
                             <button onClick={() => setShowContactModal(true)} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">
                                Share Contact Info for Future Games
                            </button>
                        )}
                    </div>
                )}

                <div className="bg-green-800 p-4 sm:p-6 rounded-lg border-4 border-green-900">
                    <h3 className="text-white font-bold mb-4">Your Hand ({currentHandForDisplay > 4 ? "Fourth Quarter" : ["First Quarter", "Second Quarter", "Third Quarter", "Fourth Quarter"][currentHandForDisplay - 1]})</h3>
                    <div className="grid grid-cols-5 gap-1 sm:gap-4">
                        {Array.from({ length: 10 }).map((_, index) => {
                            const card = sortedHand[index];
                            return <Card key={index} card={card} facedown={!card} isBestHand={card && best5CardIds.has(card.id)} />;
                        })}
                    </div>
                </div>

                <div className="mt-8">
                    <h3 className="text-2xl font-bold text-white mb-4">Current Standings (Top 5)</h3>
                    <div className="space-y-2">
                        {rankedPlayers.slice(0, 5).map((p, index) => (
                            <div key={p.uid} className="bg-gray-900 p-3 rounded-lg text-white flex justify-between items-center">
                                <div className="flex items-center">
                                    <span className="text-lg font-bold w-8">{index + 1}.</span>
                                    <p className="font-bold">{p.username}</p>
                                </div>
                                <p className="text-cyan-300">{p.handDetails.rank > 0 ? p.handDetails.name : 'No Cards'}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {myHandHistory.length > 0 && (
                    <div className="mt-8">
                        <h3 className="text-2xl font-bold text-white mb-4">Your Previous Hands</h3>
                        <ul className="space-y-4 text-gray-300 max-h-60 overflow-y-auto pr-2">
                            {myHandHistory.map(h => {
                                const wasWinner = game.handWinners?.find(winner => winner.hand === h.hand)?.winner === playerData.username;
                                return (
                                <li key={h.hand} className="bg-gray-900 p-4 rounded-lg">
                                    <p>Quarter {h.hand}: You had a {h.handName}. {wasWinner && <span className="text-yellow-400 font-bold">(Winner!)</span>}</p>
                                    <div className="flex gap-1 mt-2">
                                        {h.cards && h.cards.map(c => (
                                            <div key={c.id || c.image} className="bg-white p-1 rounded-sm shadow-md">
                                                <img src={c.image} alt={`${c.value} of ${c.suit}`} className="w-10 h-auto rounded-sm" />
                                            </div>
                                        ))}
                                    </div>
                                </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                {game.handWinners && game.handWinners.length > 0 && (
                    <div className="mt-8">
                        <h3 className="text-2xl font-bold text-white mb-4">Game History</h3>
                        <ul className="space-y-4 text-gray-300 max-h-60 overflow-y-auto pr-2">
                            {game.handWinners.map(h => (
                                <li key={h.hand} className="bg-gray-900 p-4 rounded-lg">
                                    <p>Quarter {h.hand}: <strong>{h.winner}</strong> won with a {h.winningHand}.</p>
                                    <div className="flex gap-1 mt-2">
                                        {h.cards && h.cards.map(c => (
                                            <div key={c.id || c.image} className="bg-white p-1 rounded-sm shadow-md">
                                                <img src={c.image} alt={`${c.value} of ${c.suit}`} className="w-10 h-auto rounded-sm" />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-3">
                                        {commentaries[h.hand] ? (
                                            <p className="text-sm italic text-cyan-300 p-2 bg-cyan-900/50 rounded">{commentaries[h.hand]}</p>
                                        ) : (
                                            <button onClick={() => handleGetCommentary(h)} disabled={loadingCommentary === h.hand} className="text-xs bg-purple-600 hover:bg-purple-700 text-white font-bold py-1 px-2 rounded disabled:bg-purple-400">
                                                {loadingCommentary === h.hand ? '...' : '✨ Get Commentary'}
                                            </button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="flex justify-between items-center mt-8">
                    <button onClick={() => setPage('activeGames')} className="text-gray-400 hover:text-white">&larr; Back to Game Lobby</button>
                    <div className="flex items-center gap-4">
                         <button
                            onClick={() => setIsMuted(!isMuted)}
                            className={`p-2 rounded-full ${isMuted ? 'bg-red-600' : 'bg-green-600'}`}
                            aria-label={isMuted ? "Unmute sounds" : "Mute sounds"}
                         >
                            {isMuted ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15zM17 17l-6-6m0 6l6-6" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 6v12m0 0l-4-4m4 4l4-4M6 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h2m11.536-4.536a9 9 0 010 12.728" />
                                </svg>
                            )}
                        </button>
                        <button onClick={() => setShowHandsModal(true)} className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg">View Winning Hands</button>
                    </div>
                </div>
            </div>
            <WinningHandsModal isOpen={showHandsModal} onClose={() => setShowHandsModal(false)} />
            <ContactInfoModal
                isOpen={showContactModal}
                onClose={() => setShowContactModal(false)}
                onSubmit={handleShareContactInfo}
            />
        </>
    );
};

const WinningHandsModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    const handRankings = [
        { name: 'Royal Flush', description: 'A, K, Q, J, 10, all of the same suit.' },
        { name: 'Straight Flush', description: 'Five cards in a sequence, all in the same suit.' },
        { name: 'Four of a Kind', description: 'All four cards of the same rank.' },
        { name: 'Full House', description: 'Three of a kind with a pair.' },
        { name: 'Flush', description: 'Any five cards of the same suit, but not in a sequence.' },
        { name: 'Straight', description: 'Five cards in a sequence, but not of the same suit.' },
        { name: 'Three of a Kind', description: 'Three cards of the same rank.' },
        { name: 'Two Pair', description: 'Two different pairs.' },
        { name: 'One Pair', description: 'Two cards of the same rank.' },
        { name: 'High Card', description: 'When you haven\'t made any of the hands above, the highest card plays.' },
    ];

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 p-8 rounded-lg shadow-xl max-w-lg w-full max-h-full overflow-y-auto">
                <h2 className="text-2xl font-bold text-yellow-400 mb-4">Poker Hand Rankings</h2>
                <ul className="space-y-3 text-gray-300">
                    {handRankings.map(hand => (
                        <li key={hand.name}>
                            <strong className="text-white">{hand.name}:</strong> {hand.description}
                        </li>
                    ))}
                </ul>
                <div className="text-center mt-6">
                    <button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">Close</button>
                </div>
            </div>
        </div>
    );
};

const HostContactsPage = ({ setPage, user }) => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!user?.uid) return;
        const contactsRef = collection(db, `artifacts/${appId}/users/${user.uid}/contacts`);
        const q = query(contactsRef);

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const contactsList = [];
            querySnapshot.forEach((doc) => {
                contactsList.push({ id: doc.id, ...doc.data() });
            });
            setContacts(contactsList);
            setLoading(false);
        }, (err) => {
            console.error("Error fetching contacts:", err);
            setError("Could not load contacts.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [user?.uid]);

    return (
        <>
            <div className="w-full max-w-4xl bg-gray-800/80 backdrop-blur-sm p-8 rounded-lg shadow-2xl">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-3xl font-bold text-white">My Player Contacts</h2>
                    <button onClick={() => setPage('hostDashboard')} className="text-sm text-gray-400 hover:text-white">&larr; Back to Dashboard</button>
                </div>
                {loading && <p>Loading contacts...</p>}
                {error && <p className="bg-red-500 text-white p-2 rounded mb-4">{error}</p>}
                {!loading && contacts.length === 0 && (
                    <p className="text-gray-400">No players have shared their contact information with you yet. Players get the option to share after a game is finished.</p>
                )}
                {!loading && contacts.length > 0 && (
                    <div className="space-y-4">
                        {contacts.map(contact => (
                            <div key={contact.id} className="bg-gray-700 p-4 rounded-lg">
                                <p className="font-bold text-lg text-white">{contact.username}</p>
                                {contact.email && <p className="text-sm text-gray-300">Email: {contact.email}</p>}
                                {contact.phone && <p className="text-sm text-gray-300">Phone: {contact.phone}</p>}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};

// --- New Account Management Page ---
const AccountManagementPage = ({ setPage, user, setUser }) => {
    
    // --- State for Profile ---
    const [username, setUsername] = useState(user?.username || '');
    const [hostAlias, setHostAlias] = useState(user?.alias || '');
    const [profileError, setProfileError] = useState('');
    const [profileSuccess, setProfileSuccess] = useState('');

    // --- State for Password ---
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    const handleUpdatePlayerUsername = async (e) => {
        e.preventDefault();
        setProfileError('');
        setProfileSuccess('');
        if (!username.trim()) {
            setProfileError("Username cannot be empty.");
            return;
        }
        try {
            const userDocRef = doc(db, `artifacts/${appId}/users`, user.uid);
            await updateDoc(userDocRef, { username: username });
            setUser(prev => ({...prev, username: username}));
            setProfileSuccess("Username updated successfully!");
        } catch (err) {
            setProfileError("Error updating username: " + err.message);
        }
    };

    const handleUpdateHostAlias = async (e) => {
        e.preventDefault();
        setProfileError('');
        setProfileSuccess('');
        const trimmedAlias = hostAlias.trim();

        if (!trimmedAlias) {
            setProfileError("Alias cannot be empty.");
            return;
        }
        if (trimmedAlias === user.alias) {
            setProfileError("This is already your saved alias.");
            return;
        }

        const aliasRef = doc(db, "publicAliases", trimmedAlias);
        const userDocRef = doc(db, `artifacts/${appId}/users`, user.uid);

        try {
            await runTransaction(db, async (transaction) => {
                const aliasDoc = await transaction.get(aliasRef);
                if (aliasDoc.exists()) {
                    throw new Error("This alias is already taken. Please choose another.");
                }

                if (user.alias) {
                    const oldAliasRef = doc(db, "publicAliases", user.alias);
                    transaction.delete(oldAliasRef);
                }

                transaction.set(aliasRef, { uid: user.uid });
                transaction.set(userDocRef, { alias: trimmedAlias }, { merge: true });
            });
            setUser(prev => ({...prev, alias: trimmedAlias}));
            setProfileSuccess("Alias saved successfully!");

        } catch (e) {
            console.error("Error saving alias:", e);
            setProfileError(e.message || "Could not save alias.");
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');

        if (newPassword.length < 8) {
            setPasswordError("New password must be at least 8 characters long.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError("New passwords do not match.");
            return;
        }

        const currentUser = auth.currentUser;
        if (!currentUser || !currentUser.email) {
             setPasswordError("Could not find authenticated user.");
             return;
        }
        
        try {
            const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
            await reauthenticateWithCredential(currentUser, credential);
            
            // Re-authentication successful, now update password
            await updatePassword(currentUser, newPassword);
            setPasswordSuccess("Password updated successfully!");
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            console.error(err);
            setPasswordError("Error changing password. Please check your current password.");
        }
    };
    
    const isHostOrAdmin = user.role === 'host' || user.role === 'admin';

    return (
        <div className="w-full max-w-2xl bg-gray-800/80 backdrop-blur-sm p-8 rounded-lg shadow-2xl">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-white">Account Management</h2>
                <button onClick={() => setPage('home')} className="text-sm text-gray-400 hover:text-white">&larr; Back to Home</button>
            </div>

            {/* --- Profile Section --- */}
            <div className="bg-gray-700 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-bold text-white mb-4">Update Profile</h3>
                {profileSuccess && <p className="bg-green-500 text-white p-2 rounded mb-4 text-sm">{profileSuccess}</p>}
                {profileError && <p className="bg-red-500 text-white p-2 rounded mb-4 text-sm">{profileError}</p>}
                
                {isHostOrAdmin ? (
                    <form onSubmit={handleUpdateHostAlias}>
                        <label className="block text-white mb-1">Host Alias</label>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={hostAlias}
                                onChange={(e) => setHostAlias(e.target.value)}
                                placeholder="Set your Host Alias"
                                className="w-full bg-gray-600 text-white p-3 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg">
                                Save
                            </button>
                        </div>
                    </form>
                ) : (
                     <form onSubmit={handleUpdatePlayerUsername}>
                        <label className="block text-white mb-1">Username</label>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Enter your username"
                                className="w-full bg-gray-600 text-white p-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg">
                                Save
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {/* --- Change Password Section --- */}
            <div className="bg-gray-700 p-6 rounded-lg">
                 <h3 className="text-xl font-bold text-white mb-4">Change Password</h3>
                 {passwordSuccess && <p className="bg-green-500 text-white p-2 rounded mb-4 text-sm">{passwordSuccess}</p>}
                 {passwordError && <p className="bg-red-500 text-white p-2 rounded mb-4 text-sm">{passwordError}</p>}
                 <form onSubmit={handleChangePassword} className="space-y-4">
                     <div>
                        <label className="block text-white mb-1">Current Password</label>
                        <input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="w-full bg-gray-600 text-white p-3 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                        />
                    </div>
                     <div>
                        <label className="block text-white mb-1">New Password</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full bg-gray-600 text-white p-3 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                        />
                    </div>
                     <div>
                        <label className="block text-white mb-1">Confirm New Password</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full bg-gray-600 text-white p-3 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                        />
                    </div>
                    <div className="text-right">
                        <button type="submit" className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 px-6 rounded-lg">
                            Update Password
                        </button>
                    </div>
                 </form>
            </div>
        </div>
    );
};


// --- Main App Component ---
export default function App() {
    const [page, setPage] = useState('home');
    const [user, setUser] = useState(null);
    const [gameId, setGameId] = useState(null);
    const [authReady, setAuthReady] = useState(false);
    const [filterHostId, setFilterHostId] = useState(null);
    const [installEvent, setInstallEvent] = useState(null);

    useEffect(() => {
        const handleBeforeInstallPrompt = (event) => {
            event.preventDefault();
            setInstallEvent(event);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = () => {
        if (installEvent) {
            installEvent.prompt();
            installEvent.userChoice.then((choiceResult) => {
                if (choiceResult.outcome === 'accepted') {
                    console.log('User accepted the A2HS prompt');
                } else {
                    console.log('User dismissed the A2HS prompt');
                }
                setInstallEvent(null);
            });
        }
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const hostId = urlParams.get('hostId');
        if (hostId) {
            setFilterHostId(hostId);
            setPage('activeGames');
        }

        const script = document.createElement('script');
        script.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-7229301570706115";
        script.async = true;
        script.crossOrigin = "anonymous";
        document.head.appendChild(script);

        const meta = document.createElement('meta');
        meta.name = "google-adsense-account";
        meta.content = "ca-pub-7229301570706115";
        document.head.appendChild(meta);

        const qrScript = document.createElement('script');
        qrScript.src = "https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js";
        document.head.appendChild(qrScript);

        return () => {
          document.head.removeChild(script);
          document.head.removeChild(meta);
          document.head.removeChild(qrScript);
        };
      }, []);

    useEffect(() => {
        if (!isConfigValid) { setAuthReady(true); return; }
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                if (!firebaseUser.isAnonymous) {
                    const userDocRef = doc(db, `artifacts/${appId}/users`, firebaseUser.uid);
                    const userDoc = await getDoc(userDocRef);
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        setUser({ 
                            uid: firebaseUser.uid, 
                            email: firebaseUser.email,
                            ...userData 
                        });
                        setPage('home'); // Send all logged-in users to the home page hub

                    }
                } else {
                    setUser({ role: 'guest', uid: firebaseUser.uid });
                }
            } else {
                setUser(null);
                setPage('home');
                try {
                    await signInAnonymously(auth);
                } catch (error) {
                    console.error("Anonymous sign-in failed after logout", error);
                }
            }
            setAuthReady(true);
        });
        return () => unsubscribe();
    }, []);

    const renderPage = () => {
        if (!isConfigValid) { return <ConfigurationError />; }
        switch (page) {
            case 'hostLogin': return <HostPortal setPage={setPage} />;
            case 'playerLogin': return <PlayerAuthPortal setPage={setPage} />;
            case 'howToOnline': return <HowToPlayOnline setPage={setPage} />;
            case 'howToManual': return <HowToPlayManually setPage={setPage} />;
            case 'activeGames': return <ActiveGamesList setPage={setPage} user={user} setGameId={setGameId} setUser={setUser} filterHostId={filterHostId} />;
            case 'hostDashboard': return <HostDashboard setPage={setPage} user={user} setGameId={setGameId} />;
            case 'adminPanel': return <AdminPanel setPage={setPage} currentUser={user} />;
            case 'contacts': return <HostContactsPage setPage={setPage} user={user} />;
            case 'account': return <AccountManagementPage setPage={setPage} user={user} setUser={setUser} />;
            case 'gameManagement': return <GameManagementPage setPage={setPage} gameId={gameId} user={user} />;
            case 'gamePage': return <GamePage setPage={setPage} user={user} gameId={gameId} />;
            case 'home':
            default: return <HomePage setPage={setPage} user={user} installPrompt={handleInstallClick} />;
        }
    };

    if (!authReady) { return <div className="text-white text-2xl flex items-center justify-center min-h-screen">Loading...</div> }

    return (
        <div className="bg-gray-900 text-white min-h-screen w-full flex items-center justify-center p-4" style={{
            backgroundImage: "url('/background.jpg')",
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundColor: 'black'
        }}>
            <div className="w-full flex flex-col items-center">
                <Header />
                {renderPage()}
            </div>
        </div>
    );
}

const AdminPanel = ({ setPage, currentUser }) => {
    const [games, setGames] = useState([]);
    const [users, setUsers] = useState([]);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEndGameModal, setShowEndGameModal] = useState(false);
    const [gameToModify, setGameToModify] = useState(null);

    useEffect(() => {
        const gamesQuery = query(collection(db, `artifacts/${appId}/public/data/games`));
        const unsubscribeGames = onSnapshot(gamesQuery, (querySnapshot) => {
            const gamesList = [];
            querySnapshot.forEach((doc) => {
                gamesList.push({ id: doc.id, ...doc.data() });
            });
            setGames(gamesList);
        }, (err) => {
            console.error(err);
            setError("Could not fetch games data.");
        });

        const usersQuery = query(collection(db, `artifacts/${appId}/users`));
        const unsubscribeUsers = onSnapshot(usersQuery, (querySnapshot) => {
            const usersList = [];
            querySnapshot.forEach((doc) => {
                usersList.push({ id: doc.id, ...doc.data() });
            });
            setUsers(usersList);
        }, (err) => {
            console.error(err);
            setError("Could not fetch user data.");
        });

        return () => {
            unsubscribeGames();
            unsubscribeUsers();
        };
    }, []);

    const handleRoleChange = async (userId, newRole) => {
        const userRef = doc(db, `artifacts/${appId}/users`, userId);
        try {
            await updateDoc(userRef, { role: newRole });
            setMessage(`User role updated to ${newRole}.`);
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            console.error("Error updating role:", err);
            setError(`Failed to update role: ${err.message}`);
        }
    };

    const confirmEndGame = (game) => {
        setGameToModify(game);
        setShowEndGameModal(true);
    };

    const handleEndGame = async () => {
        if(!gameToModify) return;
        const gameRef = doc(db, `artifacts/${appId}/public/data/games`, gameToModify.id);
        await updateDoc(gameRef, { status: 'finished' });
        setShowEndGameModal(false);
        setGameToModify(null);
    };

    const confirmDeleteGame = (game) => {
        setGameToModify(game);
        setShowDeleteModal(true);
    };

    const handleDeleteGame = async () => {
        if (!gameToModify) return;
        const gameRef = doc(db, `artifacts/${appId}/public/data/games`, gameToModify.id);
        await deleteDoc(gameRef);
        setShowDeleteModal(false);
        setGameToModify(null);
    };

    const handleLogout = async () => {
        await signOut(auth);
        setPage('home');
    }

    return (
        <>
            <div className="w-full max-w-6xl bg-gray-800/80 backdrop-blur-sm p-8 rounded-lg shadow-2xl">
                <h2 className="text-3xl font-bold text-white mb-6">Admin Panel</h2>
                {error && <p className="bg-red-500 text-white p-2 rounded mb-4">{error}</p>}
                {message && <p className="bg-green-500 text-white p-2 rounded mb-4">{message}</p>}

                <div className="mb-8">
                    <h3 className="text-2xl font-bold text-yellow-400 mb-3">User Management</h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                        {users.map(user => (
                             <div key={user.id} className="bg-gray-700 p-3 rounded-lg flex justify-between items-center">
                                 <div>
                                     <p className="text-white font-bold">{user.username || user.alias || 'Unnamed User'}</p>
                                     <p className="text-xs text-gray-400">{user.email || 'No Email'}</p>
                                 </div>
                                 <div className="flex items-center gap-2">
                                     <span className="text-sm text-gray-300">Role:</span>
                                     <select
                                         value={user.role || 'player'}
                                         onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                         disabled={currentUser && user.id === currentUser.uid}
                                         className="bg-gray-800 text-white rounded p-1 border border-gray-600 disabled:opacity-50"
                                     >
                                         <option value="player">Player</option>
                                         <option value="host">Host</option>
                                         <option value="admin">Admin</option>
                                     </select>
                                 </div>
                             </div>
                        ))}
                    </div>
                </div>

                <h3 className="text-2xl font-bold text-yellow-400 mb-3">All Active Games</h3>
                <div className="space-y-4">
                    {games.map(game => (
                        <div key={game.id} className="bg-gray-700 p-4 rounded-lg">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-bold text-white">{game.gameName}</h3>
                                    <p className="text-sm text-gray-300">Host: {game.hostAlias || game.hostEmail}</p>
                                    <p className={`text-sm font-bold ${game.status === 'finished' ? 'text-red-400' : 'text-green-400'}`}>Status: {game.status}</p>
                                </div>
                                <div className="flex gap-2">
                                    {game.status !== 'finished' &&
                                        <button onClick={() => confirmEndGame(game)} className="bg-yellow-600 hover:bg-yellow-700 text-black text-sm font-bold py-1 px-3 rounded">End Game</button>
                                    }
                                    <button onClick={() => confirmDeleteGame(game)} className="bg-red-600 hover:bg-red-700 text-white text-sm font-bold py-1 px-3 rounded">Delete</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex justify-between items-center mt-8">
                    <button onClick={() => setPage('hostDashboard')} className="text-gray-400 hover:text-white">&larr; Back to Dashboard</button>
                    <button onClick={handleLogout} className="text-gray-400 hover:text-white">Logout Admin</button>
                </div>
            </div>
            <ConfirmationModal
                isOpen={showEndGameModal}
                onConfirm={handleEndGame}
                onCancel={() => setShowEndGameModal(false)}
                message={`Are you sure you want to end the game "${gameToModify?.gameName}"?`}
            />
            <ConfirmationModal
                isOpen={showDeleteModal}
                onConfirm={handleDeleteGame}
                onCancel={() => setShowDeleteModal(false)}
                message={`Are you sure you want to permanently delete the game "${gameToModify?.gameName}"?`}
            />
        </>
    );
};
